import React, { useEffect, useState, useContext, useMemo, useCallback } from 'react';
import { Box, Typography, Button, Paper, Card, CardMedia, CardContent, CardActions, Grid, Snackbar, Alert, CircularProgress, Chip, Avatar, Tabs, Tab, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Menu, MenuItem, IconButton, ListItemIcon, ListItemText, FormControl, FormLabel, RadioGroup, FormControlLabel, Radio, Checkbox, FormControlLabel as MuiFormControlLabel, List, ListItem, ListItemText as MuiListItemText, Stepper, Step, StepLabel, TableSortLabel, Select, Backdrop, Fade, InputAdornment, Slider, Divider } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AddIcon from '@mui/icons-material/Add';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import SettingsIcon from '@mui/icons-material/Settings';
import LogoutIcon from '@mui/icons-material/Logout';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import RefreshIcon from '@mui/icons-material/Refresh';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import SortIcon from '@mui/icons-material/Sort';
import QrCodeIcon from '@mui/icons-material/QrCode';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import FilterListIcon from '@mui/icons-material/FilterList';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { AuthContext } from '../App';
import api, { calculateLexileLevel } from '../utils/api';
import { useNavigate, useLocation } from 'react-router-dom';
import QRCode from 'qrcode';
import LineVideoManager from '../components/LineVideoManager';
import HeroVideoManager from '../components/HeroVideoManager';

const passageImages = {
  '680d44b61d62a26057bf97dd': '/Kilauea.png',
  '680d44b61d62a26057bf97de': '/Sea-Creatures-and-Sea-Life.png',
};

function getPassageImage(passage) {
  // Remove trailing /api if present, and trailing slash
  const backendUrl = (process.env.REACT_APP_API_URL || '').replace(/\/api$/, '').replace(/\/$/, '');
  if (passage && passage.image) {
    return `${backendUrl}/passages/${passage.image}`;
  }
  if (passage && passage._id && passageImages[passage._id]) {
    return passageImages[passage._id];
  }
  return '/logo192.png';
}

function getExcerpt(text) {
  if (!text) return '';
  return text.length > 80 ? text.slice(0, 80) + '...' : text;
}

function SemiCircleProgress({ value }) {
  const radius = 50;
  const stroke = 8;
  const normalizedRadius = radius - stroke / 2;
  const circumference = Math.PI * normalizedRadius;
  const progress = (value / 100) * circumference;
  return (
    <Box width="100%" display="flex" flexDirection="column" alignItems="center" justifyContent="center">
      <svg
        viewBox={`0 0 ${radius * 2} ${radius + 10}`}
        width="100%"
        height="auto"
        style={{ maxWidth: 120, display: 'block' }}
      >
        <path
          d={`M${radius},${radius} m-${normalizedRadius},0 a${normalizedRadius},${normalizedRadius} 0 1,1 ${normalizedRadius * 2},0`}
          fill="none"
          stroke="#e0e0e0"
          strokeWidth={stroke}
        />
        <path
          d={`M${radius},${radius} m-${normalizedRadius},0 a${normalizedRadius},${normalizedRadius} 0 1,1 ${normalizedRadius * 2},0`}
          fill="none"
          stroke="#ffd54f"
          strokeWidth={stroke}
          strokeDasharray={`${progress},${circumference}`}
          strokeLinecap="round"
        />
      </svg>
      <Typography mt={-1} fontSize={22} fontWeight="bold" color="#ffd54f">{value}%</Typography>
    </Box>
  );
}

export default function Dashboard() {
  const { teacher, logout } = useContext(AuthContext);
  const [passages, setPassages] = useState([]);
  const [tests, setTests] = useState([]);
  const [sequences, setSequences] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const location = useLocation();
  const [tab, setTab] = useState(location.state?.defaultTab || 0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedPassage, setSelectedPassage] = useState(null);
  const [childName, setChildName] = useState('');
  const [gradingMethod, setGradingMethod] = useState('whisper');
  const [anchorEl, setAnchorEl] = useState(null);
  const navigate = useNavigate();
  const [createPassageOpen, setCreatePassageOpen] = useState(false);
  const [newPassageName, setNewPassageName] = useState('');
  const [newPassageText, setNewPassageText] = useState('');
  const [newPassageImage, setNewPassageImage] = useState(null);
  const [newPassageImagePreview, setNewPassageImagePreview] = useState(null);
  const [uploadingPassage, setUploadingPassage] = useState(false);
  const [processingTests, setProcessingTests] = useState(new Set());
  const [refreshing, setRefreshing] = useState(false);
  const [sequenceDialogOpen, setSequenceDialogOpen] = useState(false);
  const [selectedPassages, setSelectedPassages] = useState([]);
  const [sequenceStep, setSequenceStep] = useState(0);
  const [sequenceChildName, setSequenceChildName] = useState('');
  const [sequenceGradingMethod, setSequenceGradingMethod] = useState('whisper');
  const [creatingSequence, setCreatingSequence] = useState(false);
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [qrCodeImage, setQrCodeImage] = useState('');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [passageToDelete, setPassageToDelete] = useState(null);
  const [deletingPassage, setDeletingPassage] = useState(false);
  const [editPassageOpen, setEditPassageOpen] = useState(false);
  const [passageToEdit, setPassageToEdit] = useState(null);
  const [editPassageName, setEditPassageName] = useState('');
  const [editPassageText, setEditPassageText] = useState('');
  const [editPassageType, setEditPassageType] = useState('assessment');
  const [editPassageImage, setEditPassageImage] = useState(null);
  const [editPassageImagePreview, setEditPassageImagePreview] = useState(null);
  const [updatingPassage, setUpdatingPassage] = useState(false);
  const [newPassageType, setNewPassageType] = useState('assessment');
  const [newPassageGradeLevel, setNewPassageGradeLevel] = useState('');
  const [newPassageStoryType, setNewPassageStoryType] = useState('');
  const [newPassageSource, setNewPassageSource] = useState('');
  const [editPassageGradeLevel, setEditPassageGradeLevel] = useState('');

  const [editPassageLexileLevel, setEditPassageLexileLevel] = useState('');
  const [calculatingLexile, setCalculatingLexile] = useState(false);
  const [editPassageStoryType, setEditPassageStoryType] = useState('');
  const [editPassageSource, setEditPassageSource] = useState('');
  
  // New state for tracking text changes and lexile calculation status
  const [originalEditText, setOriginalEditText] = useState('');
  const [editTextChanged, setEditTextChanged] = useState(false);
  const [lexileNeedsCalculation, setLexileNeedsCalculation] = useState(false);
  const [editLineVideos, setEditLineVideos] = useState([]);
  const [editHeroVideo, setEditHeroVideo] = useState(null);

  // New state for saved sequences functionality
  const [savedSequences, setSavedSequences] = useState([]);
  const [saveSequenceMode, setSaveSequenceMode] = useState(false);
  const [sequenceTitle, setSequenceTitle] = useState('');
  const [sequenceDescription, setSequenceDescription] = useState('');
  const [savingSequence, setSavingSequence] = useState(false);

  // Filters state - passages
  const [passageSearchQuery, setPassageSearchQuery] = useState('');
  const [filterGradeLevels, setFilterGradeLevels] = useState([]);
  const [filterPassageType, setFilterPassageType] = useState(''); // '', 'assessment', 'story'
  const [filterStoryTypes, setFilterStoryTypes] = useState([]);
  const [filterSources, setFilterSources] = useState([]);
  const [filterLexileBucket, setFilterLexileBucket] = useState('any'); // 'any','unknown','0-199','200-499','500-799','800+'

  // Search state - sequences
  const [sequenceSearchQuery, setSequenceSearchQuery] = useState('');
  
  // Results filters state
  const [resultsSearchQuery, setResultsSearchQuery] = useState('');
  const [resultsFiltersExpanded, setResultsFiltersExpanded] = useState(false);
  const [filterResultsStatus, setFilterResultsStatus] = useState([]);
  const [filterResultsType, setFilterResultsType] = useState([]); // 'test', 'sequence'
  const [filterResultsGradeLevels, setFilterResultsGradeLevels] = useState([]);
  const [filterResultsPassageTypes, setFilterResultsPassageTypes] = useState([]);
  const [filterResultsSources, setFilterResultsSources] = useState([]);
  const [filterResultsScoreRange, setFilterResultsScoreRange] = useState([0, 100]);
  const [filterResultsDateRange, setFilterResultsDateRange] = useState({ start: '', end: '' });
  const [filterResultsTimePeriod, setFilterResultsTimePeriod] = useState('all'); // 'all', 'today', 'week', 'month', 'quarter'
  const [filterResultsGradingMethod, setFilterResultsGradingMethod] = useState([]);
  
  // UI state
  const [filtersExpanded, setFiltersExpanded] = useState(false);

  const standaloneTests = useMemo(() => tests.filter(test => !test.sequenceId), [tests]);

  const calculateWordCount = (text) => {
    if (!text || !text.trim()) return 0;
    return text.trim().split(/\s+/).length;
  };


  // Helpers for filtering
  const resetPassageFilters = () => {
    setPassageSearchQuery('');
    setFilterGradeLevels([]);
    setFilterPassageType('');
    setFilterStoryTypes([]);
    setFilterSources([]);
    setFilterLexileBucket('any');
  };

  const lexileInBucket = (lexileLevel, bucket) => {
    if (!bucket || bucket === 'any') return true;
    if (bucket === 'unknown') return !lexileLevel || Number.isNaN(parseInt(lexileLevel, 10));
    const n = parseInt(lexileLevel, 10);
    if (Number.isNaN(n)) return false;
    if (bucket === '0-199') return n >= 0 && n <= 199;
    if (bucket === '200-499') return n >= 200 && n <= 499;
    if (bucket === '500-799') return n >= 500 && n <= 799;
    if (bucket === '800+') return n >= 800;
    return true;
  };

  const filteredPassages = useMemo(() => {
    const q = passageSearchQuery.trim().toLowerCase();
    return passages.filter(p => {
      if (q && !(p.title?.toLowerCase().includes(q) || p.text?.toLowerCase().includes(q))) return false;
      if (filterGradeLevels.length > 0 && !filterGradeLevels.includes(p.gradeLevel)) return false;
      if (filterPassageType && p.passageType !== filterPassageType) return false;
      if (filterStoryTypes.length > 0) {
        if (p.passageType !== 'story') return false;
        if (!p.storyType || !filterStoryTypes.includes(p.storyType)) return false;
      }
      if (filterSources.length > 0 && !filterSources.includes(p.source)) return false;
      if (!lexileInBucket(p.lexileLevel, filterLexileBucket)) return false;
      return true;
    });
  }, [passages, passageSearchQuery, filterGradeLevels, filterPassageType, filterStoryTypes, filterSources, filterLexileBucket]);

  const filteredSavedSequences = useMemo(() => {
    const q = sequenceSearchQuery.trim().toLowerCase();
    if (!q) return savedSequences;
    return savedSequences.filter(s => s.title?.toLowerCase().includes(q));
  }, [savedSequences, sequenceSearchQuery]);

  // Get combined and sorted table data
  const getSortedTableData = useCallback(() => {
    const allItems = [...standaloneTests, ...sequences].map(item => ({
      ...item,
      type: item.passages ? 'sequence' : 'test' // Add type identifier
    }));
    
    return allItems.sort((a, b) => {
      const multiplier = sortOrder === 'desc' ? -1 : 1;
      
      if (sortBy === 'date') {
        return multiplier * (new Date(a.createdAt) - new Date(b.createdAt));
      } else if (sortBy === 'name') {
        const aName = a.type === 'sequence' ? a.childName : a.childName || '';
        const bName = b.type === 'sequence' ? b.childName : b.childName || '';
        return multiplier * aName.localeCompare(bName);
      } else if (sortBy === 'status') {
        const aStatus = a.type === 'sequence' ? a.status : a.status;
        const bStatus = b.type === 'sequence' ? b.status : b.status;
        return multiplier * aStatus.localeCompare(bStatus);
      }
      
      return 0;
    });
  }, [standaloneTests, sequences, sortBy, sortOrder]);

  // Filtered results data
  const filteredResults = useMemo(() => {
    const allItems = getSortedTableData();
    const q = resultsSearchQuery.trim().toLowerCase();
    
    return allItems.filter(item => {
      // Text search
      if (q) {
        const searchTargets = [
          item.childName || '',
          item.type === 'sequence' 
            ? item.passages.map(p => p.passageId?.title).join(' ')
            : item.passageId?.title || '',
        ].join(' ').toLowerCase();
        
        if (!searchTargets.includes(q)) return false;
      }
      
      // Status filter
      if (filterResultsStatus.length > 0) {
        const status = getTestStatus(item);
        if (!filterResultsStatus.includes(status)) return false;
      }
      
      // Type filter
      if (filterResultsType.length > 0) {
        if (!filterResultsType.includes(item.type)) return false;
      }
      
      // Grade level filter
      if (filterResultsGradeLevels.length > 0) {
        const itemGrades = getItemGradeLevels(item);
        const hasMatchingGrade = itemGrades.some(grade => filterResultsGradeLevels.includes(grade));
        if (!hasMatchingGrade) return false;
      }
      
      // Passage type filter
      if (filterResultsPassageTypes.length > 0) {
        const itemTypes = getItemPassageTypes(item);
        const hasMatchingType = itemTypes.some(type => filterResultsPassageTypes.includes(type));
        if (!hasMatchingType) return false;
      }
      
      // Source filter
      if (filterResultsSources.length > 0) {
        const itemSources = getItemSources(item);
        const hasMatchingSource = itemSources.some(source => filterResultsSources.includes(source));
        if (!hasMatchingSource) return false;
      }
      
      // Score range filter
      const score = getTestScore(item);
      if (score !== null) {
        if (score < filterResultsScoreRange[0] || score > filterResultsScoreRange[1]) {
          return false;
        }
      } else if (filterResultsScoreRange[0] > 0 || filterResultsScoreRange[1] < 100) {
        // If no score available but filter is not default range, exclude
        return false;
      }
      
      // Time period filter
      if (!isWithinTimePeriod(item.createdAt, filterResultsTimePeriod)) {
        return false;
      }
      
      // Date range filter
      if (!isWithinDateRange(item.createdAt, filterResultsDateRange)) {
        return false;
      }
      
      // Grading method filter (if applicable)
      if (filterResultsGradingMethod.length > 0) {
        const gradingMethod = item.gradingMethod || 'whisper';
        if (!filterResultsGradingMethod.includes(gradingMethod)) return false;
      }
      
      return true;
    });
  }, [
    getSortedTableData,
    resultsSearchQuery, filterResultsStatus, filterResultsType,
    filterResultsGradeLevels, filterResultsPassageTypes, filterResultsSources,
    filterResultsScoreRange, filterResultsDateRange, filterResultsTimePeriod,
    filterResultsGradingMethod
  ]);

  // Get active filter count
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (passageSearchQuery.trim()) count++;
    if (filterGradeLevels.length > 0) count++;
    if (filterPassageType) count++;
    if (filterStoryTypes.length > 0) count++;
    if (filterSources.length > 0) count++;
    if (filterLexileBucket !== 'any') count++;
    return count;
  }, [passageSearchQuery, filterGradeLevels, filterPassageType, filterStoryTypes, filterSources, filterLexileBucket]);

  // Get active results filter count
  const activeResultsFilterCount = useMemo(() => {
    let count = 0;
    if (resultsSearchQuery.trim()) count++;
    if (filterResultsStatus.length > 0) count++;
    if (filterResultsType.length > 0) count++;
    if (filterResultsGradeLevels.length > 0) count++;
    if (filterResultsPassageTypes.length > 0) count++;
    if (filterResultsSources.length > 0) count++;
    if (filterResultsScoreRange[0] > 0 || filterResultsScoreRange[1] < 100) count++;
    if (filterResultsDateRange.start || filterResultsDateRange.end) count++;
    if (filterResultsTimePeriod !== 'all') count++;
    if (filterResultsGradingMethod.length > 0) count++;
    return count;
  }, [
    resultsSearchQuery, filterResultsStatus, filterResultsType,
    filterResultsGradeLevels, filterResultsPassageTypes, filterResultsSources,
    filterResultsScoreRange, filterResultsDateRange, filterResultsTimePeriod,
    filterResultsGradingMethod
  ]);

  // Results filter helpers
  const resetResultsFilters = () => {
    setResultsSearchQuery('');
    setFilterResultsStatus([]);
    setFilterResultsType([]);
    setFilterResultsGradeLevels([]);
    setFilterResultsPassageTypes([]);
    setFilterResultsSources([]);
    setFilterResultsScoreRange([0, 100]);
    setFilterResultsDateRange({ start: '', end: '' });
    setFilterResultsTimePeriod('all');
    setFilterResultsGradingMethod([]);
  };

  function isWithinTimePeriod(dateStr, period) {
    if (period === 'all') return true;
    const date = new Date(dateStr);
    const now = new Date();
    const daysDiff = (now - date) / (1000 * 60 * 60 * 24);
    
    switch (period) {
      case 'today': return daysDiff <= 1;
      case 'week': return daysDiff <= 7;
      case 'month': return daysDiff <= 30;
      case 'quarter': return daysDiff <= 90;
      default: return true;
    }
  }

  function isWithinDateRange(dateStr, range) {
    if (!range.start && !range.end) return true;
    const date = new Date(dateStr);
    const start = range.start ? new Date(range.start) : new Date('1900-01-01');
    const end = range.end ? new Date(range.end) : new Date('2100-12-31');
    return date >= start && date <= end;
  }

  function getTestScore(item) {
    if (item.type === 'sequence') {
      // For sequences, calculate average score of completed passages
      if (!item.completedPassages || item.completedPassages.length === 0) return null;
      const totalScore = item.completedPassages.reduce((sum, p) => sum + (p.result?.accuracy || 0), 0);
      return Math.round(totalScore / item.completedPassages.length);
    } else {
      // For single tests
      return item.result?.accuracy || null;
    }
  }

  function getTestStatus(item) {
    if (item.type === 'sequence') {
      switch (item.status) {
        case 'created': return 'not_started';
        case 'in_progress': return 'in_progress';
        case 'completed': return 'completed';
        default: return 'unknown';
      }
    } else {
      if (item.status !== 'attempted') return 'not_attempted';
      if (!item.result || item.result.accuracy === undefined) return 'processing';
      return 'completed';
    }
  }

  function getItemGradeLevels(item) {
    if (item.type === 'sequence') {
      // Get unique grade levels from all passages in sequence
      const gradeLevels = item.passages.map(p => p.passageId?.gradeLevel).filter(Boolean);
      return [...new Set(gradeLevels)];
    } else {
      return item.passageId?.gradeLevel ? [item.passageId.gradeLevel] : [];
    }
  }

  function getItemPassageTypes(item) {
    if (item.type === 'sequence') {
      const types = item.passages.map(p => p.passageId?.passageType).filter(Boolean);
      return [...new Set(types)];
    } else {
      return item.passageId?.passageType ? [item.passageId.passageType] : [];
    }
  }

  function getItemSources(item) {
    if (item.type === 'sequence') {
      const sources = item.passages.map(p => p.passageId?.source).filter(Boolean);
      return [...new Set(sources)];
    } else {
      return item.passageId?.source ? [item.passageId.source] : [];
    }
  }

  // Get filter chips for display
  const getFilterChips = () => {
    const chips = [];
    if (passageSearchQuery.trim()) {
      chips.push({ 
        key: 'search', 
        label: `"${passageSearchQuery}"`, 
        onDelete: () => setPassageSearchQuery('') 
      });
    }
    if (filterGradeLevels.length > 0) {
      chips.push({ 
        key: 'grade', 
        label: `Grade: ${filterGradeLevels.join(', ')}`, 
        onDelete: () => setFilterGradeLevels([]) 
      });
    }
    if (filterPassageType) {
      chips.push({ 
        key: 'type', 
        label: `Type: ${filterPassageType}`, 
        onDelete: () => setFilterPassageType('') 
      });
    }
    if (filterStoryTypes.length > 0) {
      chips.push({ 
        key: 'story', 
        label: `Genre: ${filterStoryTypes.join(', ')}`, 
        onDelete: () => setFilterStoryTypes([]) 
      });
    }
    if (filterSources.length > 0) {
      chips.push({ 
        key: 'source', 
        label: `Source: ${filterSources.join(', ')}`, 
        onDelete: () => setFilterSources([]) 
      });
    }
    if (filterLexileBucket !== 'any') {
      chips.push({ 
        key: 'lexile', 
        label: `Lexile: ${filterLexileBucket === 'unknown' ? 'Unknown' : filterLexileBucket}`, 
        onDelete: () => setFilterLexileBucket('any') 
      });
    }
    return chips;
  };

  // Get results filter chips for display
  const getResultsFilterChips = () => {
    const chips = [];
    
    if (resultsSearchQuery.trim()) {
      chips.push({
        key: 'search',
        label: `"${resultsSearchQuery}"`,
        onDelete: () => setResultsSearchQuery('')
      });
    }
    
    if (filterResultsStatus.length > 0) {
      const statusLabels = {
        'not_attempted': 'Not Attempted',
        'not_started': 'Not Started',
        'in_progress': 'In Progress',
        'processing': 'Processing',
        'completed': 'Completed',
        'unknown': 'Unknown'
      };
      chips.push({
        key: 'status',
        label: `Status: ${filterResultsStatus.map(s => statusLabels[s] || s).join(', ')}`,
        onDelete: () => setFilterResultsStatus([])
      });
    }
    
    if (filterResultsType.length > 0) {
      const typeLabels = { 'test': 'Single Passage', 'sequence': 'Sequence' };
      chips.push({
        key: 'type',
        label: `Type: ${filterResultsType.map(t => typeLabels[t] || t).join(', ')}`,
        onDelete: () => setFilterResultsType([])
      });
    }
    
    if (filterResultsGradeLevels.length > 0) {
      chips.push({
        key: 'grade',
        label: `Grade: ${filterResultsGradeLevels.join(', ')}`,
        onDelete: () => setFilterResultsGradeLevels([])
      });
    }
    
    if (filterResultsPassageTypes.length > 0) {
      chips.push({
        key: 'passageType',
        label: `Passage: ${filterResultsPassageTypes.join(', ')}`,
        onDelete: () => setFilterResultsPassageTypes([])
      });
    }
    
    if (filterResultsSources.length > 0) {
      chips.push({
        key: 'source',
        label: `Source: ${filterResultsSources.join(', ')}`,
        onDelete: () => setFilterResultsSources([])
      });
    }
    
    if (filterResultsScoreRange[0] > 0 || filterResultsScoreRange[1] < 100) {
      chips.push({
        key: 'score',
        label: `Score: ${filterResultsScoreRange[0]}%-${filterResultsScoreRange[1]}%`,
        onDelete: () => setFilterResultsScoreRange([0, 100])
      });
    }
    
    if (filterResultsTimePeriod !== 'all') {
      const periodLabels = {
        'today': 'Today',
        'week': 'This Week',
        'month': 'This Month',
        'quarter': 'This Quarter'
      };
      chips.push({
        key: 'timePeriod',
        label: `Period: ${periodLabels[filterResultsTimePeriod]}`,
        onDelete: () => setFilterResultsTimePeriod('all')
      });
    }
    
    if (filterResultsDateRange.start || filterResultsDateRange.end) {
      const start = filterResultsDateRange.start || 'Start';
      const end = filterResultsDateRange.end || 'End';
      chips.push({
        key: 'dateRange',
        label: `Date: ${start} - ${end}`,
        onDelete: () => setFilterResultsDateRange({ start: '', end: '' })
      });
    }
    
    if (filterResultsGradingMethod.length > 0) {
      chips.push({
        key: 'grading',
        label: `Grading: ${filterResultsGradingMethod.join(', ')}`,
        onDelete: () => setFilterResultsGradingMethod([])
      });
    }
    
    return chips;
  };


  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleCreateClass = () => {
    console.log('Create Class clicked');
    handleMenuClose();
  };

  const handleImportPassage = () => {
    console.log('Import new passage clicked');
    handleMenuClose();
  };

  const handleSettings = () => {
    navigate('/settings');
    handleMenuClose();
  };

  const handleAdmin = () => {
    navigate('/admin');
    handleMenuClose();
  };
  const handleLogout = () => {
    logout();
    handleMenuClose();
  };

  // Check processing status for tests
  const checkProcessingStatus = async (testId, linkId) => {
    try {
      const response = await api.get(`/api/tests/${linkId}/processing-status`);
      
      if (response.data.status === 'completed') {
        // Test has been processed
        // Refresh test data, which will in turn update the processing list
        fetchTests();
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error checking processing status:', error);
      // Fallback to debug-attempts endpoint
      try {
        const fallbackResponse = await api.get(`/api/tests/${linkId}/debug-attempts`);
        
        if (fallbackResponse.data.totalAttempts > 0) {
          fetchTests();
          return true;
        }
        return false;
      } catch (fallbackError) {
        console.error('Fallback processing status check failed:', fallbackError);
        return false;
      }
    }
  };

  const fetchTests = async () => {
    try {
      const [testRes, sequenceRes] = await Promise.all([
        api.get('/api/tests'),
        api.get('/api/sequences')
      ]);
      
      setTests(testRes.data);
      setSequences(sequenceRes.data);
      const processing = new Set(testRes.data.filter(t => t.status === 'attempted' && !t.result).map(t => t._id));
      setProcessingTests(processing);
    } catch (error) {
      console.error('Error fetching tests and sequences:', error);
    }
  };

  const fetchSavedSequences = async () => {
    try {
      const response = await api.get('/api/saved-sequences');
      setSavedSequences(response.data);
    } catch (error) {
      console.error('Error fetching saved sequences:', error);
    }
  };

  const handleAssignSavedSequence = (sequence) => {
    // Create a dialog to assign this saved sequence to a student
    setSelectedPassages(sequence.passages.map(p => p.passageId));
    setSaveSequenceMode(false);
    setSequenceStep(1);
    setSequenceChildName('');
    setSequenceTitle('');
    setSequenceDescription('');
    setSequenceDialogOpen(true);
  };

  const handleViewSequenceDetails = (sequence) => {
    // For now, just show the assign dialog in view mode
    // In future, could create a detailed view modal
    setSelectedPassages(sequence.passages.map(p => p.passageId));
    setSaveSequenceMode(false);
    setSequenceStep(0);
    setSequenceChildName('');
    setSequenceDialogOpen(true);
  };

  const handleSaveSequence = async () => {
    if (!sequenceTitle.trim() || selectedPassages.length === 0) return;
    
    setSavingSequence(true);
    try {
      // Validate passages before sending
      const validPassages = selectedPassages.filter(p => p && p._id);
      if (validPassages.length === 0) {
        throw new Error('No valid passages selected');
      }
      
      const payload = {
        title: sequenceTitle,
        description: sequenceDescription,
        passages: validPassages.map((p, index) => ({ 
          passageId: p._id, 
          sequenceOrder: index + 1 
        }))
      };
      
      console.log('Saving sequence with payload:', payload);
      
      await api.post('/api/saved-sequences', payload);
      
      setSnackbar({ open: true, message: 'Sequence saved successfully!', severity: 'success' });
      setSequenceDialogOpen(false);
      
      // Reset form
      setSequenceTitle('');
      setSequenceDescription('');
      setSaveSequenceMode(false);
      
      // Refresh saved sequences
      fetchSavedSequences();
    } catch (error) {
      console.error('Error saving sequence:', error);
      console.error('Error details:', error.response?.data);
      setSnackbar({ 
        open: true, 
        message: error.response?.data?.error || 'Failed to save sequence', 
        severity: 'error' 
      });
    }
    setSavingSequence(false);
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([fetchTests(), fetchSavedSequences()]);
    setTimeout(() => {
        setRefreshing(false);
        setSnackbar({ open: true, message: 'Results updated!', severity: 'info' });
    }, 500);
  };

  useEffect(() => {
    async function fetchData() {
      try {
        const passRes = await api.get('/api/passages');
        setPassages(passRes.data);
        await fetchTests(); // This will fetch tests and set processing tests
        await fetchSavedSequences(); // Fetch saved sequences
        setLoading(false);
      } catch (error) {
        setSnackbar({ open: true, message: 'Failed to load data', severity: 'error' });
        setLoading(false);
      }
    }
    fetchData();
  }, []);

  // Polling for processing tests
  useEffect(() => {
    if (processingTests.size === 0) return;

    const interval = setInterval(async () => {
      const testArray = Array.from(processingTests);
      for (const testId of testArray) {
        const test = tests.find(t => t._id === testId);
        if (test) {
          await checkProcessingStatus(testId, test.linkId);
        }
      }
    }, 3000); // Check every 3 seconds

    return () => clearInterval(interval);
  }, [processingTests, tests]);

  // Stats
  const totalTests = standaloneTests.length + sequences.length;
  const attemptedTests = standaloneTests.filter(t => t.status === 'attempted').length + 
                         sequences.filter(s => s.status === 'completed').length;
  const avgScore = standaloneTests.length
    ? Math.round(
        standaloneTests.reduce((sum, t) => sum + (t.result?.accuracy || 0), 0) /
          (standaloneTests.filter(t => t.result && t.result.accuracy).length || 1)
      )
    : 0;

  const handleCreateTestClick = (passage) => {
    setSelectedPassage(passage);
    setChildName('');
    setGradingMethod('whisper');
    setDialogOpen(true);
  };

  const handleCreateTest = async () => {
    if (!selectedPassage || !childName.trim()) return;
    setCreating(true);
    try {
      const res = await api.post('/api/tests', { 
        passageId: selectedPassage._id, 
        childName, 
        gradingMethod 
      });
      // Construct the new test object with populated passage info for immediate display
      const newTestForState = {
        ...res.data, // Include fields from backend response (_id, linkId, status, childName)
        passageId: { // Ensure passageId is an object with needed info
          _id: selectedPassage._id,
          title: selectedPassage.title
        },
        createdAt: new Date().toISOString() // Ensure it has the current timestamp
      };
      setTests([newTestForState, ...tests]);
      // Show success message
      setSnackbar({ open: true, message: 'Test created!', severity: 'success' });
      setDialogOpen(false);
    } catch {
      setSnackbar({ open: true, message: 'Failed to create test', severity: 'error' });
    }
    setCreating(false);
  };

  const handleCopy = (linkId) => {
    navigator.clipboard.writeText(`${window.location.origin}/test/${linkId}/start`);
    setSnackbar({ open: true, message: 'Link copied!', severity: 'info' });
  };

  // Handle image selection
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setNewPassageImage(file);
      setNewPassageImagePreview(URL.createObjectURL(file));
    }
  };
  const handleImageDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) {
      setNewPassageImage(file);
      setNewPassageImagePreview(URL.createObjectURL(file));
    }
  };
  const handleImageRemove = () => {
    setNewPassageImage(null);
    setNewPassageImagePreview(null);
  };

  // Create passage submit
  const handleCreatePassage = async () => {
    if (!newPassageName.trim() || !newPassageText.trim() || !newPassageGradeLevel || !newPassageSource) {
      setSnackbar({ open: true, message: 'Please fill in all required fields', severity: 'error' });
      return;
    }
    if (newPassageType === 'story' && !newPassageStoryType) {
      setSnackbar({ open: true, message: 'Story type is required for story passages', severity: 'error' });
      return;
    }
    setUploadingPassage(true);
    try {
      let res;
      if (newPassageImage) {
        // Upload image and passage in one request (multipart/form-data)
        const formData = new FormData();
        formData.append('title', newPassageName);
        formData.append('text', newPassageText);
        formData.append('passageType', newPassageType);
        formData.append('gradeLevel', newPassageGradeLevel);
        formData.append('source', newPassageSource);
        if (newPassageType === 'story') {
          formData.append('storyType', newPassageStoryType);
        }
        formData.append('image', newPassageImage);
        res = await api.post('/api/passages', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        setPassages([{ ...res.data, createdAt: new Date().toISOString() }, ...passages]);
      } else {
        // No image, just send data
        const passageData = {
          title: newPassageName,
          text: newPassageText,
          passageType: newPassageType,
          gradeLevel: newPassageGradeLevel,
          source: newPassageSource
        };
        if (newPassageType === 'story') {
          passageData.storyType = newPassageStoryType;
        }
        res = await api.post('/api/passages', passageData);
        setPassages([{ ...res.data, createdAt: new Date().toISOString() }, ...passages]);
      }

      // Close create dialog
      setCreatePassageOpen(false);

      // Reset create form
      setNewPassageName('');
      setNewPassageText('');
      setNewPassageType('assessment');
      setNewPassageGradeLevel('');
      setNewPassageStoryType('');
      setNewPassageSource('');
      setNewPassageImage(null);
      setNewPassageImagePreview(null);

      // Open edit dialog with the created passage so user can add videos
      const createdPassage = res.data;
      setPassageToEdit(createdPassage);
      setEditPassageName(createdPassage.title);
      setEditPassageText(createdPassage.text);
      setOriginalEditText(createdPassage.text);
      setEditTextChanged(false);
      setLexileNeedsCalculation(false);
      setEditPassageType(createdPassage.passageType || 'assessment');
      setEditPassageGradeLevel(createdPassage.gradeLevel || '');
      setEditPassageStoryType(createdPassage.storyType || '');
      setEditPassageSource(createdPassage.source || '');
      setEditPassageImage(null);
      setEditPassageImagePreview(createdPassage.image ? getPassageImage(createdPassage) : null);
      setEditPassageLexileLevel(createdPassage.lexileLevel || '');
      setEditLineVideos(createdPassage.lineVideos || []);
      setEditPassageOpen(true);

      setSnackbar({ open: true, message: 'Passage created! You can now add video instructions.', severity: 'success' });
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Failed to create passage';
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });
    }
    setUploadingPassage(false);
  };

  const handleSequenceAssignClick = () => {
    setSelectedPassages([]);
    setSequenceStep(0);
    setSequenceChildName('');
    setSequenceGradingMethod('whisper');
    setSaveSequenceMode(false);
    setSequenceTitle('');
    setSequenceDescription('');
    setSequenceDialogOpen(true);
  };

  const handlePassageToggle = (passage) => {
    const existingIndex = selectedPassages.findIndex(p => p._id === passage._id);
    if (existingIndex >= 0) {
      setSelectedPassages(selectedPassages.filter((_, index) => index !== existingIndex));
    } else {
      setSelectedPassages([...selectedPassages, passage]);
    }
  };

  const getPassageSequenceNumber = (passageId) => {
    const index = selectedPassages.findIndex(p => p._id === passageId);
    return index >= 0 ? index + 1 : null;
  };

  const handleSequenceNext = () => {
    if (sequenceStep === 0 && selectedPassages.length > 0) {
      setSequenceStep(1);
    }
  };

  const handleSequenceBack = () => {
    if (sequenceStep === 1) {
      setSequenceStep(0);
    }
  };

  const handleCreateSequence = async () => {
    if (!sequenceChildName.trim() || selectedPassages.length === 0) return;
    setCreatingSequence(true);
    try {
      const res = await api.post('/api/sequences', { 
        passages: selectedPassages.map((p, index) => ({ passageId: p._id, sequenceOrder: index + 1 })),
        childName: sequenceChildName,
        gradingMethod: sequenceGradingMethod
      });
      setSnackbar({ open: true, message: 'Sequence created successfully!', severity: 'success' });
      setSequenceDialogOpen(false);
      // Refresh data to show new sequence
      fetchTests();
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to create sequence', severity: 'error' });
    }
    setCreatingSequence(false);
  };

  const handleSort = (property) => {
    const isAsc = sortBy === property && sortOrder === 'asc';
    setSortOrder(isAsc ? 'desc' : 'asc');
    setSortBy(property);
  };

  const generateQRCode = async (url) => {
    try {
      const qrDataUrl = await QRCode.toDataURL(url, {
        width: 300,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        }
      });
      return qrDataUrl;
    } catch (error) {
      console.error('Error generating QR code:', error);
      return null;
    }
  };

  const handleQRCodeClick = async (linkId, isSequence = false) => {
    const url = isSequence 
      ? `${window.location.origin}/sequence/${linkId}/start`
      : `${window.location.origin}/test/${linkId}/start`;
    
    setQrCodeUrl(url);
    const qrImage = await generateQRCode(url);
    if (qrImage) {
      setQrCodeImage(qrImage);
      setQrModalOpen(true);
    }
  };

  const handleQRModalClose = () => {
    setQrModalOpen(false);
    setQrCodeUrl('');
    setQrCodeImage('');
  };

  const handleDeleteClick = (passage) => {
    setPassageToDelete(passage);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!passageToDelete) return;
    setDeletingPassage(true);
    try {
      await api.patch(`/api/passages/${passageToDelete._id}/archive`, {});
      setPassages(passages.filter(p => p._id !== passageToDelete._id));
      setSnackbar({ open: true, message: 'Passage archived successfully!', severity: 'success' });
      setDeleteConfirmOpen(false);
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to archive passage', severity: 'error' });
    }
    setDeletingPassage(false);
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false);
    setPassageToDelete(null);
  };

  const handleEditClick = (passage) => {
    console.log('🚀 EDIT CLICK - Passage data:', {
      id: passage._id,
      title: passage.title,
      lexileLevel: passage.lexileLevel
    });
    
    setPassageToEdit(passage);
    setEditPassageName(passage.title);
    setEditPassageText(passage.text);
    setOriginalEditText(passage.text); // Track original text
    setEditTextChanged(false); // Reset text changed flag
    setLexileNeedsCalculation(false); // Reset lexile calculation flag
    setEditPassageType(passage.passageType || 'assessment');
    setEditPassageGradeLevel(passage.gradeLevel || '');
    setEditPassageStoryType(passage.storyType || '');
    setEditPassageSource(passage.source || '');
    setEditPassageImage(null);
    setEditPassageImagePreview(passage.image ? getPassageImage(passage) : null);
    setEditPassageLexileLevel(passage.lexileLevel || '');
    setEditLineVideos(passage.lineVideos || []);
    setEditHeroVideo(passage.heroVideo || null);

    // NO AUTO-CALCULATION! User controls when lexile is calculated
    console.log('✅ Edit opened - NO auto-calculation. User will control lexile calculation via Update button.');
    
    setEditPassageOpen(true);
  };

  const handleEditImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setEditPassageImage(file);
      setEditPassageImagePreview(URL.createObjectURL(file));
    }
  };

  const handleEditImageRemove = () => {
    setEditPassageImage(null);
    setEditPassageImagePreview(passageToEdit?.image ? getPassageImage(passageToEdit) : null);
  };

  const handleEditPassage = async () => {
    if (!editPassageName.trim() || !editPassageText.trim() || !editPassageGradeLevel || !editPassageSource || !passageToEdit) {
      setSnackbar({ open: true, message: 'Please fill in all required fields', severity: 'error' });
      return;
    }
    if (editPassageType === 'story' && !editPassageStoryType) {
      setSnackbar({ open: true, message: 'Story type is required for story passages', severity: 'error' });
      return;
    }
    
    setUpdatingPassage(true);
    
    try {
      let finalLexileLevel = editPassageLexileLevel;
      
      // SIMPLE: If text changed, calculate lexile first
      if (editTextChanged && editPassageText.trim()) {
        console.log('📊 Text changed - calculating Lexile level...');
        setCalculatingLexile(true);
        
        try {
          finalLexileLevel = await calculateLexileLevel(editPassageText);
          setEditPassageLexileLevel(finalLexileLevel);
          console.log('✅ Lexile calculated:', finalLexileLevel);
        } catch (lexileError) {
          console.error('❌ Lexile calculation failed:', lexileError);
          finalLexileLevel = '';
        }
        
        setCalculatingLexile(false);
      }
      
      // Build update data
      const updateData = {
        title: editPassageName,
        text: editPassageText,
        passageType: editPassageType,
        gradeLevel: editPassageGradeLevel,
        source: editPassageSource
      };
      
      if (editPassageType === 'story') {
        updateData.storyType = editPassageStoryType;
      }
      
      // Include lexile level if we have one
      if (finalLexileLevel) {
        updateData.lexileLevel = finalLexileLevel;
      }
      
      // Send update request
      let response;
      if (editPassageImage) {
        const formData = new FormData();
        Object.keys(updateData).forEach(key => {
          formData.append(key, updateData[key]);
        });
        formData.append('image', editPassageImage);
        
        response = await api.put(`/api/passages/${passageToEdit._id}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } else {
        response = await api.put(`/api/passages/${passageToEdit._id}`, updateData);
      }
      
      // Update local state
      setPassages(passages.map(p => p._id === passageToEdit._id ? response.data : p));
      
      setSnackbar({ 
        open: true, 
        message: editTextChanged && finalLexileLevel ? 'Passage and Lexile level updated!' : 'Passage updated!', 
        severity: 'success' 
      });
      setEditPassageOpen(false);
      
      // Reset state
      setEditTextChanged(false);
      setLexileNeedsCalculation(false);
      setOriginalEditText('');
      
    } catch (error) {
      console.error('Update failed:', error);
      const errorMessage = error.response?.data?.error || 'Failed to update passage';
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });
    }
    
    setUpdatingPassage(false);
  };

  if (loading) return <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh"><CircularProgress /></Box>;

  return (
    <Box p={3} minHeight="100vh" sx={{ background: 'linear-gradient(135deg, #e0f7fa 0%, #fffde7 100%)', fontFamily: 'Nunito, sans-serif' }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <Box>
            <Typography variant="h4" fontWeight={700}>Welcome, {teacher?.name}</Typography>
            <Typography color="text.secondary">Empowering young readers, one story at a time!</Typography>
          </Box>
        </Box>
        <Box display="flex" alignItems="center" gap={2}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            sx={{ fontWeight: 700, borderRadius: 3, background: 'linear-gradient(90deg, #81c784 0%, #4fc3f7 100%)', fontSize: 18, px: 3, py: 1.2, boxShadow: '0 2px 8px rgba(79,195,247,0.10)' }}
            onClick={() => setCreatePassageOpen(true)}
          >
            Import New Passage
          </Button>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<PlaylistAddIcon />}
            sx={{ fontWeight: 700, borderRadius: 3, background: 'linear-gradient(90deg, #ff9800 0%, #f57c00 100%)', fontSize: 18, px: 3, py: 1.2, boxShadow: '0 2px 8px rgba(255,152,0,0.20)' }}
            onClick={handleSequenceAssignClick}
          >
            Assign Sequence
          </Button>
          <IconButton
            onClick={handleMenuOpen}
            size="large"
            sx={{
              p: 0.5,
              borderRadius: '50%',
              position: 'relative',
              '&:hover': {
                bgcolor: 'rgba(79, 195, 247, 0.1)',
              },
            }}
          >
            <Avatar sx={{ width: 56, height: 56, bgcolor: '#4fc3f7', fontSize: 32 }}>
              {teacher?.name?.[0]}
            </Avatar>
            <KeyboardArrowDownIcon sx={{ position: 'absolute', bottom: 0, right: 0, fontSize: 20, color: 'rgba(0,0,0,0.5)', background: 'rgba(255,255,255,0.7)', borderRadius: '50%', p: 0.2 }} />
          </IconButton>
        </Box>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          PaperProps={{
            sx: {
              borderRadius: 2,
              mt: 1.5,
              minWidth: 220,
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              background: '#f9fafc',
              '& .MuiMenuItem-root': {
                py: 1.5,
                px: 2.5,
                fontSize: 16,
                fontWeight: 600,
                '& .MuiSvgIcon-root': {
                  fontSize: 22,
                  color: 'text.secondary',
                  mr: 1.5,
                },
              },
            },
          }}
        >
          {/* <MenuItem onClick={handleCreateClass}>
            <ListItemIcon>
              <AddIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Create Class</ListItemText>
          </MenuItem> */}
          {/* <MenuItem onClick={handleImportPassage}>
            <ListItemIcon>
              <UploadFileIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Import new passage</ListItemText>
          </MenuItem> */}
          {/* <MenuItem onClick={handleSettings}>
            <ListItemIcon>
              <SettingsIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Settings</ListItemText>
          </MenuItem> */}

          {teacher && teacher.role === 'admin' && (
            <MenuItem onClick={handleAdmin}>
              <ListItemIcon>
                <AdminPanelSettingsIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Admin Panel</ListItemText>
            </MenuItem>
          )}
          <MenuItem onClick={handleLogout}>
            <ListItemIcon>
              <LogoutIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Logout</ListItemText>
          </MenuItem>
        </Menu>
      </Box>

      {/* Stats */}
      <Grid container spacing={3} mb={2}>
        <Grid item xs={12} sm={4} md={4} lg={4} display="flex">
          <Paper elevation={3} sx={{ p: 3, borderRadius: 3, textAlign: 'center', flex: 1, minWidth: 0, width: 260, maxWidth: 260, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant="h6" color="#4fc3f7">Total Passages Sent</Typography>
            <Typography variant="h3" fontWeight={700}>{totalTests}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={4} md={4} lg={4} display="flex">
          <Paper elevation={3} sx={{ p: 3, borderRadius: 3, textAlign: 'center', flex: 1, minWidth: 0, width: 260, maxWidth: 260, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant="h6" color="#81c784">Passages Attempted</Typography>
            <Typography variant="h3" fontWeight={700}>{attemptedTests}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={4} md={4} lg={4} display="flex">
          <Paper elevation={3} sx={{ p: 3, borderRadius: 3, textAlign: 'center', flex: 1, minWidth: 0, width: 260, maxWidth: 260, background: '#f9fafc', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant="h6" color="#ffd54f">Avg. Score</Typography>
            <SemiCircleProgress value={avgScore} />
          </Paper>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} centered textColor="primary" indicatorColor="primary" sx={{
          '& .MuiTab-root': { fontWeight: 700, fontSize: 18, textTransform: 'none' },
        }}>
          <Tab label="Reading Passages" />
          <Tab label="Sequences" />
          <Tab label="Results" />
        </Tabs>
      </Box>

      {/* Tab Panels */}
      {tab === 0 && (
        <>
          {/* Search and Filter Header */}
          <Grid container spacing={3} mb={3} justifyContent="center">
            <Grid item xs={12} display="flex" justifyContent="center">
              <Box sx={{ width: '100%', maxWidth: 1200, px: { xs: 2, sm: 3 } }}>
                {/* Search and Filter Controls */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                  <TextField
                    placeholder="Search passages..."
                    value={passageSearchQuery}
                    onChange={(e) => setPassageSearchQuery(e.target.value)}
                    sx={{
                      width: { xs: '100%', sm: 320 },
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                        fontSize: 14,
                        height: 40,
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 1)',
                        },
                        '&.Mui-focused': {
                          backgroundColor: 'rgba(255, 255, 255, 1)',
                          boxShadow: '0 0 0 2px rgba(79, 195, 247, 0.2)',
                        }
                      }
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ color: '#4fc3f7', fontSize: 20 }} />
                        </InputAdornment>
                      )
                    }}
                  />

                  <Button
                    variant={filtersExpanded ? "contained" : "outlined"}
                    startIcon={<FilterListIcon />}
                    endIcon={filtersExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    onClick={() => setFiltersExpanded(!filtersExpanded)}
                    sx={{
                      borderRadius: 2,
                      fontWeight: 600,
                      height: 40,
                      minWidth: 140,
                      ...(filtersExpanded ? {
                        background: 'linear-gradient(90deg, #4fc3f7 0%, #81c784 100%)',
                        boxShadow: '0 2px 8px rgba(79,195,247,0.3)',
                      } : {
                        borderColor: '#4fc3f7',
                        color: '#4fc3f7',
                        '&:hover': {
                          borderColor: '#29b6f6',
                          backgroundColor: 'rgba(79, 195, 247, 0.04)',
                        }
                      })
                    }}
                  >
                    Filters {activeFilterCount > 0 && `(${activeFilterCount})`}
                  </Button>
                </Box>

                {/* Active Filter Chips */}
                {activeFilterCount > 0 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    {getFilterChips().map(chip => (
                      <Chip
                        key={chip.key}
                        label={chip.label}
                        onDelete={chip.onDelete}
                        variant="filled"
                        color="primary"
                        size="small"
                        sx={{
                          borderRadius: 1.5,
                          fontWeight: 500,
                          fontSize: 12,
                          height: 28,
                          '& .MuiChip-deleteIcon': {
                            fontSize: 16,
                            color: 'rgba(255,255,255,0.8)',
                            '&:hover': {
                              color: 'rgba(255,255,255,1)',
                            }
                          }
                        }}
                      />
                    ))}
                    <Button
                      variant="text"
                      color="secondary"
                      size="small"
                      onClick={resetPassageFilters}
                      sx={{ 
                        fontWeight: 600,
                        fontSize: 12,
                        minWidth: 'auto',
                        px: 1,
                        textDecoration: 'underline',
                        '&:hover': {
                          backgroundColor: 'rgba(156, 39, 176, 0.04)',
                        }
                      }}
                    >
                      Clear All
                    </Button>
                  </Box>
                )}

                {/* Expandable Filter Panel */}
                <Fade in={filtersExpanded}>
                  <Paper 
                    elevation={3} 
                    sx={{ 
                      mt: 2, 
                      p: 3, 
                      borderRadius: 3,
                      background: 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(248,250,252,0.95) 100%)',
                      border: '1px solid rgba(79, 195, 247, 0.2)',
                      display: filtersExpanded ? 'block' : 'none'
                    }}
                  >
                <Typography variant="h6" fontWeight={600} color="text.primary" mb={3} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FilterListIcon color="primary" />
                  Filter Passages
                </Typography>
                
                <Grid container spacing={3}>
                  {/* Passage Type */}
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth>
                      <Typography variant="subtitle2" fontWeight={600} color="text.primary" mb={1}>
                        📝 Passage Type
                      </Typography>
                      <Select
                        value={filterPassageType}
                        onChange={(e) => setFilterPassageType(e.target.value)}
                        displayEmpty
                        sx={{ 
                          borderRadius: 2,
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(79, 195, 247, 0.3)',
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(79, 195, 247, 0.5)',
                          }
                        }}
                      >
                        <MenuItem value="">All Types</MenuItem>
                        <MenuItem value="assessment">📋 Assessment</MenuItem>
                        <MenuItem value="story">📚 Story</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Grade Level */}
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth>
                      <Typography variant="subtitle2" fontWeight={600} color="text.primary" mb={1}>
                        🎓 Grade Level
                      </Typography>
                      <Select
                        multiple
                        value={filterGradeLevels}
                        onChange={(e) => setFilterGradeLevels(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                        renderValue={(selected) => selected.length === 0 ? 'All Grades' : selected.join(', ')}
                        displayEmpty
                        sx={{ 
                          borderRadius: 2,
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(76, 175, 80, 0.3)',
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(76, 175, 80, 0.5)',
                          }
                        }}
                      >
                        {['K','1','2','3','4','5','6','7','8','9','10','11','12'].map(g => (
                          <MenuItem key={g} value={g}>
                            <Checkbox 
                              checked={filterGradeLevels.indexOf(g) > -1} 
                              sx={{ color: '#4caf50', '&.Mui-checked': { color: '#4caf50' } }}
                            />
                            <MuiListItemText primary={g === 'K' ? 'Kindergarten' : `Grade ${g}`} />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Genre (Story Types) */}
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth>
                      <Typography variant="subtitle2" fontWeight={600} color="text.primary" mb={1}>
                        🎭 Genre
                      </Typography>
                      <Select
                        multiple
                        value={filterStoryTypes}
                        onChange={(e) => setFilterStoryTypes(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                        renderValue={(selected) => selected.length === 0 ? 'All Genres' : selected.join(', ')}
                        displayEmpty
                        disabled={filterPassageType === 'assessment'}
                        sx={{ 
                          borderRadius: 2,
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(156, 39, 176, 0.3)',
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(156, 39, 176, 0.5)',
                          }
                        }}
                      >
                        {['Controlled Decodable','Expository','Fiction'].map(t => (
                          <MenuItem key={t} value={t}>
                            <Checkbox 
                              checked={filterStoryTypes.indexOf(t) > -1}
                              sx={{ color: '#9c27b0', '&.Mui-checked': { color: '#9c27b0' } }}
                            />
                            <MuiListItemText primary={t} />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Lexile Level */}
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth>
                      <Typography variant="subtitle2" fontWeight={600} color="text.primary" mb={1}>
                        📊 Lexile Level
                      </Typography>
                      <Select
                        value={filterLexileBucket}
                        onChange={(e) => setFilterLexileBucket(e.target.value)}
                        sx={{ 
                          borderRadius: 2,
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(255, 152, 0, 0.3)',
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(255, 152, 0, 0.5)',
                          }
                        }}
                      >
                        <MenuItem value="any">Any Level</MenuItem>
                        <MenuItem value="unknown">❓ Unknown</MenuItem>
                        <MenuItem value="0-199">🟢 0–199L (Beginner)</MenuItem>
                        <MenuItem value="200-499">🔵 200–499L (Elementary)</MenuItem>
                        <MenuItem value="500-799">🟡 500–799L (Middle)</MenuItem>
                        <MenuItem value="800+">🔴 800L+ (Advanced)</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Source */}
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <Typography variant="subtitle2" fontWeight={600} color="text.primary" mb={1}>
                        📚 Source
                      </Typography>
                      <Select
                        multiple
                        value={filterSources}
                        onChange={(e) => setFilterSources(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                        renderValue={(selected) => selected.length === 0 ? 'All Sources' : selected.join(', ')}
                        displayEmpty
                        sx={{ 
                          borderRadius: 2,
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(63, 81, 181, 0.3)',
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(63, 81, 181, 0.5)',
                          }
                        }}
                      >
                        {['RGR Blast','RGR Countdown','RGR HD Word','RGR Boost','DIBELS','RGR Surveys','ReadWorks','Other'].map(s => (
                          <MenuItem key={s} value={s}>
                            <Checkbox 
                              checked={filterSources.indexOf(s) > -1}
                              sx={{ color: '#3f51b5', '&.Mui-checked': { color: '#3f51b5' } }}
                            />
                            <MuiListItemText primary={s} />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3, pt: 2, borderTop: '1px solid rgba(0,0,0,0.1)' }}>
                  <Typography variant="body2" color="text.secondary">
                    Showing {filteredPassages.length} of {passages.length} passages
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                      variant="outlined"
                      color="secondary"
                      onClick={resetPassageFilters}
                      sx={{ borderRadius: 2, fontWeight: 600 }}
                    >
                      Clear All Filters
                    </Button>
                    <Button
                      variant="contained"
                      onClick={() => setFiltersExpanded(false)}
                      sx={{ 
                        borderRadius: 2, 
                        fontWeight: 600,
                        background: 'linear-gradient(90deg, #4fc3f7 0%, #81c784 100%)',
                      }}
                    >
                      Apply Filters
                    </Button>
                  </Box>
                  </Box>
                  </Paper>
                </Fade>
              </Box>
            </Grid>
          </Grid>

          <Grid container spacing={3} mb={4} justifyContent="center">
          {filteredPassages.length === 0 && (
            <Grid item xs={12} display="flex" justifyContent="center">
              <Typography color="text.secondary">No passages match your filters.</Typography>
            </Grid>
          )}
          {filteredPassages.map(p => (
            <Grid item xs={12} sm={6} md={4} lg={4} key={p._id} display="flex" justifyContent="center">
              <Card sx={{
                borderRadius: 3,
                boxShadow: '0 4px 16px rgba(79,195,247,0.08)',
                width: 340,
                maxWidth: '100%',
                height: 480,
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
              }}>
                <Box sx={{ position: 'absolute', top: 8, right: 8, display: 'flex', gap: 1, zIndex: 2 }}>
                  {/* Show edit/delete buttons only if user is admin OR if teacher created this passage themselves */}
                  {(teacher.role === 'admin' || (teacher.role === 'teacher' && p.createdBy === teacher._id && !p.isAdminCreated)) && (
                    <>
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditClick(p);
                        }}
                        sx={{
                          bgcolor: 'rgba(255, 255, 255, 0.9)',
                          color: '#2196f3',
                          width: 32,
                          height: 32,
                          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                          '&:hover': {
                            bgcolor: 'rgba(33, 150, 243, 0.1)',
                            transform: 'scale(1.1)',
                          },
                          transition: 'all 0.2s ease',
                        }}
                      >
                        <EditIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteClick(p);
                        }}
                        sx={{
                          bgcolor: 'rgba(255, 255, 255, 0.9)',
                          color: '#f44336',
                          width: 32,
                          height: 32,
                          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                          '&:hover': {
                            bgcolor: 'rgba(244, 67, 54, 0.1)',
                            transform: 'scale(1.1)',
                          },
                          transition: 'all 0.2s ease',
                        }}
                      >
                        <DeleteIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </>
                  )}
                </Box>
                <CardMedia
                  component="img"
                  height="180"
                  image={getPassageImage(p)}
                  alt={p.title}
                  sx={{ objectFit: 'cover', borderTopLeftRadius: 12, borderTopRightRadius: 12 }}
                />
                <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                    <Typography variant="h6" fontWeight={700}>{p.title}</Typography>
                    <Chip 
                      label={p.passageType === 'story' ? 'Story' : 'Assessment'}
                      size="small"
                      color={p.passageType === 'story' ? 'secondary' : 'primary'}
                      sx={{ fontSize: '0.7rem', fontWeight: 600 }}
                    />
                  </Box>
                  <Typography color="text.secondary" fontSize={15} mb={1} sx={{ flexGrow: 1 }}>{getExcerpt(p.text)}</Typography>
                  <Box display="flex" gap={1} flexWrap="wrap" mt={1}>
                    {p.gradeLevel && (
                      <Chip 
                        label={`Grade ${p.gradeLevel}`}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: '0.65rem', height: '20px' }}
                      />
                    )}
                    {p.source && (
                      <Chip 
                        label={p.source}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: '0.65rem', height: '20px' }}
                      />
                    )}
                    {p.storyType && p.passageType === 'story' && (
                      <Chip 
                        label={p.storyType}
                        size="small"
                        variant="outlined"
                        color="secondary"
                        sx={{ fontSize: '0.65rem', height: '20px' }}
                      />
                    )}
                    {p.wordCount && (
                      <Chip 
                        label={`${p.wordCount} words`}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: '0.65rem', height: '20px' }}
                      />
                    )}
                    {p.lexileLevel && (
                      <Chip 
                        label={`Lexile: ${p.lexileLevel}`}
                        size="small"
                        variant="outlined"
                        color="success"
                        sx={{ fontSize: '0.65rem', height: '20px' }}
                      />
                    )}
                  </Box>
                </CardContent>
                <CardActions>
                  <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    sx={{ borderRadius: 2, fontWeight: 'bold', background: 'linear-gradient(90deg, #4fc3f7 0%, #81c784 100%)' }}
                    onClick={() => handleCreateTestClick(p)}
                    disabled={creating}
                  >
                    Assign Passage to Student
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
        </>
      )}
      
      {/* Sequences Tab */}
      {tab === 1 && (
        <>
        {/* Sequences Search */}
        <Grid container spacing={3} mb={3} justifyContent="center">
          <Grid item xs={12} display="flex" justifyContent="center">
            <Box sx={{ width: '100%', maxWidth: 1200, px: { xs: 2, sm: 3 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                <TextField
                  placeholder="Search sequence templates..."
                  value={sequenceSearchQuery}
                  onChange={(e) => setSequenceSearchQuery(e.target.value)}
                  sx={{
                    width: { xs: '100%', sm: 400 },
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      backgroundColor: 'rgba(255, 255, 255, 0.9)',
                      fontSize: 14,
                      height: 40,
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 1)',
                      },
                      '&.Mui-focused': {
                        backgroundColor: 'rgba(255, 255, 255, 1)',
                        boxShadow: '0 0 0 2px rgba(255, 152, 0, 0.2)',
                      }
                    }
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: '#ff9800', fontSize: 20 }} />
                      </InputAdornment>
                    )
                  }}
                />
                {sequenceSearchQuery && (
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: 13 }}>
                    {filteredSavedSequences.length} of {savedSequences.length} sequences
                  </Typography>
                )}
              </Box>
              
              {sequenceSearchQuery && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Chip
                    label={`"${sequenceSearchQuery}"`}
                    onDelete={() => setSequenceSearchQuery('')}
                    variant="filled"
                    color="secondary"
                    size="small"
                    sx={{
                      borderRadius: 1.5,
                      fontWeight: 500,
                      fontSize: 12,
                      height: 28,
                      '& .MuiChip-deleteIcon': {
                        fontSize: 16,
                        color: 'rgba(255,255,255,0.8)',
                        '&:hover': {
                          color: 'rgba(255,255,255,1)',
                        }
                      }
                    }}
                  />
                </Box>
              )}
            </Box>
          </Grid>
        </Grid>
        <Grid container spacing={3} mb={4} justifyContent="center">
          {savedSequences.length === 0 ? (
            <Grid item xs={12} display="flex" justifyContent="center">
              <Box textAlign="center" py={8}>
                <Typography variant="h5" fontWeight={700} color="text.secondary" mb={2}>
                  📚 No Saved Sequences Yet
                </Typography>
                <Typography variant="body1" color="text.secondary" mb={4}>
                  Create your first sequence template to reuse with multiple students!
                </Typography>
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={<PlaylistAddIcon />}
                  onClick={handleSequenceAssignClick}
                  sx={{ 
                    fontWeight: 700, 
                    borderRadius: 3, 
                    px: 4, 
                    py: 1.5, 
                    fontSize: 16,
                    background: 'linear-gradient(90deg, #ff9800 0%, #f57c00 100%)'
                  }}
                >
                  Create Your First Sequence
                </Button>
              </Box>
            </Grid>
          ) : filteredSavedSequences.length === 0 ? (
            <Grid item xs={12} display="flex" justifyContent="center">
              <Typography color="text.secondary">No sequences match your search.</Typography>
            </Grid>
          ) : (
            filteredSavedSequences.map(sequence => (
              <Grid item xs={12} sm={6} md={4} lg={4} key={sequence._id} display="flex" justifyContent="center">
                <Card sx={{
                  borderRadius: 3,
                  boxShadow: '0 4px 16px rgba(255,152,0,0.08)',
                  width: 340,
                  maxWidth: '100%',
                  height: 480,
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'relative',
                  border: '2px solid rgba(255,152,0,0.1)',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 32px rgba(255,152,0,0.15)',
                    borderColor: 'rgba(255,152,0,0.3)'
                  },
                  transition: 'all 0.3s ease'
                }}>
                  <Box sx={{ 
                    position: 'absolute', 
                    top: 12, 
                    right: 12, 
                    display: 'flex', 
                    gap: 1, 
                    zIndex: 2 
                  }}>
                    <Chip 
                      label={sequence.totalPassages}
                      color="warning"
                      size="small"
                      sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}
                    />
                  </Box>

                  <Box sx={{ 
                    background: 'linear-gradient(135deg, #fff3e0 0%, #ffcc02 100%)',
                    height: 140,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                    overflow: 'hidden'
                  }}>
                    <Box textAlign="center">
                      <PlaylistAddIcon sx={{ fontSize: 48, color: '#f57c00', mb: 1 }} />
                      <Typography variant="h6" fontWeight={700} color="#e65100">
                        {sequence.totalPassages} Passages
                      </Typography>
                    </Box>
                    <Box sx={{
                      position: 'absolute',
                      top: -20,
                      right: -20,
                      width: 80,
                      height: 80,
                      borderRadius: '50%',
                      background: 'rgba(255,255,255,0.2)',
                      opacity: 0.7
                    }} />
                  </Box>

                  <CardContent sx={{ flexGrow: 1, p: 3, display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="h6" fontWeight={700} mb={1} color="text.primary" noWrap>
                      {sequence.title}
                    </Typography>
                    
                    {sequence.description && (
                      <Typography variant="body2" color="text.secondary" mb={2} 
                        sx={{ 
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          minHeight: '2.5em',
                          flexGrow: 1
                        }}>
                        {sequence.description}
                      </Typography>
                    )}

                    <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
                      {sequence.totalStories > 0 && (
                        <Chip 
                          label={`${sequence.totalStories} Stories`}
                          size="small"
                          color="secondary"
                          variant="outlined"
                          sx={{ fontSize: '0.65rem', height: '22px' }}
                        />
                      )}
                      {sequence.totalAssessments > 0 && (
                        <Chip 
                          label={`${sequence.totalAssessments} Tests`}
                          size="small"
                          color="primary"
                          variant="outlined"
                          sx={{ fontSize: '0.65rem', height: '22px' }}
                        />
                      )}
                    </Box>

                    <Box display="flex" flexWrap="wrap" gap={0.5} mb={2}>
                      {sequence.gradeLevels.map(grade => (
                        <Chip
                          key={grade}
                          label={`Grade ${grade}`}
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: '0.6rem', height: '18px' }}
                        />
                      ))}
                    </Box>

                    <Typography variant="caption" color="text.secondary" display="block">
                      Est. {sequence.estimatedDuration} min • Created {new Date(sequence.createdAt).toLocaleDateString()}
                    </Typography>
                  </CardContent>

                  <CardActions sx={{ justifyContent: 'space-between', px: 3, pb: 3 }}>
                    <Button
                      variant="outlined"
                      color="secondary"
                      size="small"
                      onClick={() => handleAssignSavedSequence(sequence)}
                      sx={{ 
                        fontWeight: 600, 
                        borderRadius: 2, 
                        flex: 1,
                        mr: 1,
                        border: '1.5px solid #ff9800',
                        color: '#ff9800',
                        '&:hover': { 
                          bgcolor: 'rgba(255,152,0,0.1)',
                          borderColor: '#f57c00'
                        }
                      }}
                    >
                      Assign
                    </Button>
                    <Button
                      variant="contained"
                      color="warning"
                      size="small"
                      onClick={() => handleViewSequenceDetails(sequence)}
                      sx={{ 
                        fontWeight: 600, 
                        borderRadius: 2, 
                        flex: 1,
                        background: 'linear-gradient(90deg, #ff9800 0%, #f57c00 100%)'
                      }}
                    >
                      View
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))
          )}
        </Grid>
        </>
      )}
      
      {tab === 2 && (
        <>
          {/* Results Search and Filter Header */}
          <Grid container spacing={3} mb={3} justifyContent="center">
            <Grid item xs={12} display="flex" justifyContent="center">
              <Box sx={{ width: '100%', maxWidth: 1400, px: { xs: 2, sm: 3 } }}>
                {/* Search and Filter Controls */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                  <TextField
                    placeholder="Search results by student name or passage..."
                    value={resultsSearchQuery}
                    onChange={(e) => setResultsSearchQuery(e.target.value)}
                    sx={{
                      width: { xs: '100%', sm: 400 },
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                        fontSize: 14,
                        height: 40,
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 1)',
                        },
                        '&.Mui-focused': {
                          backgroundColor: 'rgba(255, 255, 255, 1)',
                          boxShadow: '0 0 0 2px rgba(33, 150, 243, 0.2)',
                        }
                      }
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ color: '#2196f3', fontSize: 20 }} />
                        </InputAdornment>
                      )
                    }}
                  />

                  <Button
                    variant={resultsFiltersExpanded ? "contained" : "outlined"}
                    startIcon={<FilterListIcon />}
                    endIcon={resultsFiltersExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    onClick={() => setResultsFiltersExpanded(!resultsFiltersExpanded)}
                    sx={{
                      borderRadius: 2,
                      fontWeight: 600,
                      height: 40,
                      minWidth: 140,
                      ...(resultsFiltersExpanded ? {
                        background: 'linear-gradient(90deg, #2196f3 0%, #64b5f6 100%)',
                        boxShadow: '0 2px 8px rgba(33,150,243,0.3)',
                      } : {
                        borderColor: '#2196f3',
                        color: '#2196f3',
                        '&:hover': {
                          borderColor: '#1976d2',
                          backgroundColor: 'rgba(33, 150, 243, 0.04)',
                        }
                      })
                    }}
                  >
                    Advanced Filters {activeResultsFilterCount > 0 && `(${activeResultsFilterCount})`}
                  </Button>

                  <Box display="flex" alignItems="center" gap={1} ml="auto">
                    <SortIcon fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">Sort:</Typography>
                    <Select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      size="small"
                      sx={{ minWidth: 120, height: 40 }}
                    >
                      <MenuItem value="date">Date</MenuItem>
                      <MenuItem value="name">Student Name</MenuItem>
                      <MenuItem value="status">Status</MenuItem>
                    </Select>
                    <IconButton 
                      size="small" 
                      onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                      sx={{ p: 0.5 }}
                    >
                      <Typography variant="body2" color="primary" fontWeight="bold">
                        {sortOrder === 'asc' ? '↑' : '↓'}
                      </Typography>
                    </IconButton>
                    <IconButton onClick={handleRefresh} disabled={refreshing} sx={{ ml: 1 }}>
                      {refreshing ? <CircularProgress size={24} /> : <RefreshIcon />}
                    </IconButton>
                  </Box>
                </Box>

                {/* Active Filter Chips */}
                {activeResultsFilterCount > 0 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                    {getResultsFilterChips().map(chip => (
                      <Chip
                        key={chip.key}
                        label={chip.label}
                        onDelete={chip.onDelete}
                        variant="filled"
                        color="primary"
                        size="small"
                        sx={{
                          borderRadius: 1.5,
                          fontWeight: 500,
                          fontSize: 12,
                          height: 28,
                          '& .MuiChip-deleteIcon': {
                            fontSize: 16,
                            color: 'rgba(255,255,255,0.8)',
                            '&:hover': {
                              color: 'rgba(255,255,255,1)',
                            }
                          }
                        }}
                      />
                    ))}
                    <Button
                      variant="text"
                      color="secondary"
                      size="small"
                      onClick={resetResultsFilters}
                      sx={{ 
                        fontWeight: 600,
                        fontSize: 12,
                        minWidth: 'auto',
                        px: 1,
                        textDecoration: 'underline',
                        '&:hover': {
                          backgroundColor: 'rgba(156, 39, 176, 0.04)',
                        }
                      }}
                    >
                      Clear All
                    </Button>
                  </Box>
                )}

                {/* Expandable Filter Panel */}
                <Fade in={resultsFiltersExpanded}>
                  <Paper 
                    elevation={3} 
                    sx={{ 
                      mt: 2, 
                      p: 4, 
                      borderRadius: 3,
                      background: 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(248,250,252,0.95) 100%)',
                      border: '1px solid rgba(33, 150, 243, 0.2)',
                      display: resultsFiltersExpanded ? 'block' : 'none'
                    }}
                  >
                    <Typography variant="h6" fontWeight={600} color="text.primary" mb={3} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <FilterListIcon color="primary" />
                      Advanced Results Filters
                    </Typography>
                    
                    <Grid container spacing={3}>
                      {/* Status Filter */}
                      <Grid item xs={12} sm={6} md={4}>
                        <FormControl fullWidth>
                          <Typography variant="subtitle2" fontWeight={600} color="text.primary" mb={1}>
                            📊 Status
                          </Typography>
                          <Select
                            multiple
                            value={filterResultsStatus}
                            onChange={(e) => setFilterResultsStatus(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                            renderValue={(selected) => selected.length === 0 ? 'All Statuses' : selected.map(s => ({
                              'not_attempted': 'Not Attempted',
                              'not_started': 'Not Started',
                              'in_progress': 'In Progress',
                              'processing': 'Processing',
                              'completed': 'Completed',
                              'unknown': 'Unknown'
                            }[s] || s)).join(', ')}
                            displayEmpty
                            sx={{ 
                              borderRadius: 2,
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'rgba(33, 150, 243, 0.3)',
                              },
                              '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'rgba(33, 150, 243, 0.5)',
                              }
                            }}
                          >
                            {[
                              { value: 'not_attempted', label: '⏳ Not Attempted', color: '#ff9800' },
                              { value: 'not_started', label: '🔲 Not Started', color: '#ff9800' },
                              { value: 'in_progress', label: '⚡ In Progress', color: '#2196f3' },
                              { value: 'processing', label: '🔄 Processing', color: '#9c27b0' },
                              { value: 'completed', label: '✅ Completed', color: '#4caf50' }
                            ].map(status => (
                              <MenuItem key={status.value} value={status.value}>
                                <Checkbox 
                                  checked={filterResultsStatus.indexOf(status.value) > -1} 
                                  sx={{ color: status.color, '&.Mui-checked': { color: status.color } }}
                                />
                                <MuiListItemText primary={status.label} />
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>

                      {/* Type Filter */}
                      <Grid item xs={12} sm={6} md={4}>
                        <FormControl fullWidth>
                          <Typography variant="subtitle2" fontWeight={600} color="text.primary" mb={1}>
                            🎯 Test Type
                          </Typography>
                          <Select
                            multiple
                            value={filterResultsType}
                            onChange={(e) => setFilterResultsType(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                            renderValue={(selected) => selected.length === 0 ? 'All Types' : selected.map(t => t === 'test' ? 'Single Passage' : 'Sequence').join(', ')}
                            displayEmpty
                            sx={{ 
                              borderRadius: 2,
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'rgba(255, 152, 0, 0.3)',
                              },
                              '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'rgba(255, 152, 0, 0.5)',
                              }
                            }}
                          >
                            <MenuItem value="test">
                              <Checkbox 
                                checked={filterResultsType.indexOf('test') > -1} 
                                sx={{ color: '#ff9800', '&.Mui-checked': { color: '#ff9800' } }}
                              />
                              <MuiListItemText primary="📄 Single Passage" />
                            </MenuItem>
                            <MenuItem value="sequence">
                              <Checkbox 
                                checked={filterResultsType.indexOf('sequence') > -1} 
                                sx={{ color: '#ff9800', '&.Mui-checked': { color: '#ff9800' } }}
                              />
                              <MuiListItemText primary="📚 Sequence" />
                            </MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>

                      {/* Grade Level Filter */}
                      <Grid item xs={12} sm={6} md={4}>
                        <FormControl fullWidth>
                          <Typography variant="subtitle2" fontWeight={600} color="text.primary" mb={1}>
                            🎓 Grade Level
                          </Typography>
                          <Select
                            multiple
                            value={filterResultsGradeLevels}
                            onChange={(e) => setFilterResultsGradeLevels(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                            renderValue={(selected) => selected.length === 0 ? 'All Grades' : selected.join(', ')}
                            displayEmpty
                            sx={{ 
                              borderRadius: 2,
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'rgba(76, 175, 80, 0.3)',
                              },
                              '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'rgba(76, 175, 80, 0.5)',
                              }
                            }}
                          >
                            {['K','1','2','3','4','5','6','7','8','9','10','11','12'].map(g => (
                              <MenuItem key={g} value={g}>
                                <Checkbox 
                                  checked={filterResultsGradeLevels.indexOf(g) > -1} 
                                  sx={{ color: '#4caf50', '&.Mui-checked': { color: '#4caf50' } }}
                                />
                                <MuiListItemText primary={g === 'K' ? 'Kindergarten' : `Grade ${g}`} />
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>

                      {/* Passage Type Filter */}
                      <Grid item xs={12} sm={6} md={4}>
                        <FormControl fullWidth>
                          <Typography variant="subtitle2" fontWeight={600} color="text.primary" mb={1}>
                            📝 Passage Type
                          </Typography>
                          <Select
                            multiple
                            value={filterResultsPassageTypes}
                            onChange={(e) => setFilterResultsPassageTypes(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                            renderValue={(selected) => selected.length === 0 ? 'All Passage Types' : selected.join(', ')}
                            displayEmpty
                            sx={{ 
                              borderRadius: 2,
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'rgba(156, 39, 176, 0.3)',
                              },
                              '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'rgba(156, 39, 176, 0.5)',
                              }
                            }}
                          >
                            <MenuItem value="assessment">
                              <Checkbox 
                                checked={filterResultsPassageTypes.indexOf('assessment') > -1} 
                                sx={{ color: '#9c27b0', '&.Mui-checked': { color: '#9c27b0' } }}
                              />
                              <MuiListItemText primary="📋 Assessment" />
                            </MenuItem>
                            <MenuItem value="story">
                              <Checkbox 
                                checked={filterResultsPassageTypes.indexOf('story') > -1} 
                                sx={{ color: '#9c27b0', '&.Mui-checked': { color: '#9c27b0' } }}
                              />
                              <MuiListItemText primary="📚 Story" />
                            </MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>

                      {/* Time Period Quick Filter */}
                      <Grid item xs={12} sm={6} md={4}>
                        <FormControl fullWidth>
                          <Typography variant="subtitle2" fontWeight={600} color="text.primary" mb={1}>
                            ⏰ Time Period
                          </Typography>
                          <Select
                            value={filterResultsTimePeriod}
                            onChange={(e) => setFilterResultsTimePeriod(e.target.value)}
                            sx={{ 
                              borderRadius: 2,
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'rgba(63, 81, 181, 0.3)',
                              },
                              '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'rgba(63, 81, 181, 0.5)',
                              }
                            }}
                          >
                            <MenuItem value="all">📅 All Time</MenuItem>
                            <MenuItem value="today">🌅 Today</MenuItem>
                            <MenuItem value="week">📈 This Week</MenuItem>
                            <MenuItem value="month">📊 This Month</MenuItem>
                            <MenuItem value="quarter">📋 This Quarter</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>

                      {/* Score Range Filter */}
                      <Grid item xs={12} sm={6} md={4}>
                        <Box>
                          <Typography variant="subtitle2" fontWeight={600} color="text.primary" mb={1}>
                            🎯 Score Range: {filterResultsScoreRange[0]}% - {filterResultsScoreRange[1]}%
                          </Typography>
                          <Box sx={{ px: 2 }}>
                            <Slider
                              value={filterResultsScoreRange}
                              onChange={(e, newValue) => setFilterResultsScoreRange(newValue)}
                              valueLabelDisplay="auto"
                              min={0}
                              max={100}
                              step={5}
                              sx={{ 
                                color: '#4caf50',
                                '& .MuiSlider-thumb': {
                                  bgcolor: '#4caf50',
                                },
                                '& .MuiSlider-track': {
                                  bgcolor: '#4caf50',
                                },
                                '& .MuiSlider-rail': {
                                  bgcolor: 'rgba(76, 175, 80, 0.2)',
                                }
                              }}
                            />
                          </Box>
                        </Box>
                      </Grid>

                      {/* Source Filter */}
                      <Grid item xs={12}>
                        <FormControl fullWidth>
                          <Typography variant="subtitle2" fontWeight={600} color="text.primary" mb={1}>
                            📚 Source
                          </Typography>
                          <Select
                            multiple
                            value={filterResultsSources}
                            onChange={(e) => setFilterResultsSources(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                            renderValue={(selected) => selected.length === 0 ? 'All Sources' : selected.join(', ')}
                            displayEmpty
                            sx={{ 
                              borderRadius: 2,
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'rgba(63, 81, 181, 0.3)',
                              },
                              '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'rgba(63, 81, 181, 0.5)',
                              }
                            }}
                          >
                            {['RGR Blast','RGR Countdown','RGR HD Word','RGR Boost','DIBELS','RGR Surveys','ReadWorks','Other'].map(s => (
                              <MenuItem key={s} value={s}>
                                <Checkbox 
                                  checked={filterResultsSources.indexOf(s) > -1}
                                  sx={{ color: '#3f51b5', '&.Mui-checked': { color: '#3f51b5' } }}
                                />
                                <MuiListItemText primary={s} />
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>

                      {/* Date Range Filter */}
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" fontWeight={600} color="text.primary" mb={1}>
                          📆 Custom Date Range
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                          <TextField
                            label="Start Date"
                            type="date"
                            value={filterResultsDateRange.start}
                            onChange={(e) => setFilterResultsDateRange({...filterResultsDateRange, start: e.target.value})}
                            InputLabelProps={{ shrink: true }}
                            sx={{ flex: 1, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                          />
                          <TextField
                            label="End Date"
                            type="date"
                            value={filterResultsDateRange.end}
                            onChange={(e) => setFilterResultsDateRange({...filterResultsDateRange, end: e.target.value})}
                            InputLabelProps={{ shrink: true }}
                            sx={{ flex: 1, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                          />
                        </Box>
                      </Grid>

                      {/* Grading Method Filter */}
                      <Grid item xs={12} sm={6}>
                        <FormControl fullWidth>
                          <Typography variant="subtitle2" fontWeight={600} color="text.primary" mb={1}>
                            🤖 Grading Method
                          </Typography>
                          <Select
                            multiple
                            value={filterResultsGradingMethod}
                            onChange={(e) => setFilterResultsGradingMethod(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                            renderValue={(selected) => selected.length === 0 ? 'All Methods' : selected.join(', ')}
                            displayEmpty
                            sx={{ 
                              borderRadius: 2,
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'rgba(121, 85, 72, 0.3)',
                              },
                              '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'rgba(121, 85, 72, 0.5)',
                              }
                            }}
                          >
                            <MenuItem value="whisper">
                              <Checkbox 
                                checked={filterResultsGradingMethod.indexOf('whisper') > -1}
                                sx={{ color: '#795548', '&.Mui-checked': { color: '#795548' } }}
                              />
                              <MuiListItemText primary="🎤 Whisper AI" />
                            </MenuItem>
                            <MenuItem value="speechace">
                              <Checkbox 
                                checked={filterResultsGradingMethod.indexOf('speechace') > -1}
                                sx={{ color: '#795548', '&.Mui-checked': { color: '#795548' } }}
                              />
                              <MuiListItemText primary="🗣️ Speechace" />
                            </MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                    </Grid>

                    <Divider sx={{ my: 3 }} />
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" color="text.secondary">
                        Showing {filteredResults.length} of {getSortedTableData().length} results
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <Button
                          variant="outlined"
                          color="secondary"
                          onClick={resetResultsFilters}
                          sx={{ borderRadius: 2, fontWeight: 600 }}
                        >
                          Clear All Filters
                        </Button>
                        <Button
                          variant="contained"
                          onClick={() => setResultsFiltersExpanded(false)}
                          sx={{ 
                            borderRadius: 2, 
                            fontWeight: 600,
                            background: 'linear-gradient(90deg, #2196f3 0%, #64b5f6 100%)',
                          }}
                        >
                          Apply Filters
                        </Button>
                      </Box>
                    </Box>
                  </Paper>
                </Fade>
              </Box>
            </Grid>
          </Grid>

          {/* Results Table */}
          <Paper elevation={3} sx={{ p: 3, borderRadius: 3, mb: 4 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
              <Typography variant="h6" fontWeight={700}>
                📊 Test Results {activeResultsFilterCount > 0 && `(${filteredResults.length} filtered)`}
              </Typography>
            </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Type</TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortBy === 'title'}
                      direction={sortBy === 'title' ? sortOrder : 'asc'}
                      onClick={() => handleSort('title')}
                    >
                      Title
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortBy === 'name'}
                      direction={sortBy === 'name' ? sortOrder : 'asc'}
                      onClick={() => handleSort('name')}
                    >
                      Student
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortBy === 'status'}
                      direction={sortBy === 'status' ? sortOrder : 'asc'}
                      onClick={() => handleSort('status')}
                    >
                      Status
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Progress</TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortBy === 'date'}
                      direction={sortBy === 'date' ? sortOrder : 'asc'}
                      onClick={() => handleSort('date')}
                    >
                      Created Date
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>QR Code</TableCell>
                  <TableCell>Link</TableCell>
                  <TableCell>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredResults.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} sx={{ textAlign: 'center', py: 8 }}>
                      <Typography variant="h6" color="text.secondary" mb={1}>
                        📭 No results match your filters
                      </Typography>
                      <Typography variant="body2" color="text.secondary" mb={3}>
                        Try adjusting your search terms or filters to see more results
                      </Typography>
                      <Button 
                        variant="outlined" 
                        onClick={resetResultsFilters}
                        startIcon={<FilterListIcon />}
                        sx={{ borderRadius: 2 }}
                      >
                        Clear All Filters
                      </Button>
                    </TableCell>
                  </TableRow>
                ) : (
                  /* Render filtered and sorted tests and sequences */
                  filteredResults.map(item => item.type === 'test' ? (
                  <TableRow key={`test-${item._id}`}>
                    <TableCell>
                      <Chip label="Single Passage" size="small" color="default" />
                    </TableCell>
                    <TableCell>{item.passageId?.title}</TableCell>
                    <TableCell>{item.childName || '-'}</TableCell>
                    <TableCell>
                      {(() => {
                        if (item.status !== 'attempted') {
                          return <Chip label="Not Attempted" color="warning" size="small" />;
                        }
                        if (!item.result || item.result.accuracy === undefined) {
                          return (
                            <Chip
                              label="Report Generating"
                              color="info"
                              size="small"
                              icon={<CircularProgress size={16} color="inherit" />}
                            />
                          );
                        }
                        return <Chip label="Report Available" color="success" size="small" />;
                      })()}
                    </TableCell>
                    <TableCell>
                      {item.result && item.result.accuracy !== undefined ? `${item.result.accuracy}%` : '-'}
                    </TableCell>
                    <TableCell>
                      {new Date(item.createdAt).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      })}
                    </TableCell>
                    <TableCell>
                      <IconButton
                        onClick={() => handleQRCodeClick(item.linkId, false)}
                        sx={{
                          color: '#4fc3f7',
                          '&:hover': {
                            backgroundColor: 'rgba(79, 195, 247, 0.1)',
                            transform: 'scale(1.1)'
                          },
                          transition: 'all 0.2s ease'
                        }}
                      >
                        <QrCodeIcon fontSize="medium" />
                      </IconButton>
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="outlined"
                        startIcon={<ContentCopyIcon />}
                        onClick={() => handleCopy(item.linkId)}
                        sx={{ borderRadius: 2 }}
                      >
                        Copy Link
                      </Button>
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={() => navigate(`/test/${item.linkId}/report`)}
                        disabled={item.status !== 'attempted'}
                        sx={{ borderRadius: 2 }}
                      >
                        View Result
                      </Button>
                    </TableCell>
                  </TableRow>
                ) : (
                  <TableRow key={`sequence-${item._id}`}>
                    <TableCell>
                      <Chip label="Sequence" size="small" color="secondary" />
                    </TableCell>
                    <TableCell>
                      {item.passages.map((p, index) => p.passageId?.title).join(', ')} 
                      <Typography variant="caption" color="text.secondary" display="block">
                        ({item.passages.length} passages)
                      </Typography>
                    </TableCell>
                    <TableCell>{item.childName}</TableCell>
                    <TableCell>
                      {(() => {
                        switch (item.status) {
                          case 'created':
                            return <Chip label="Not Started" color="warning" size="small" />;
                          case 'in_progress':
                            return <Chip label="In Progress" color="info" size="small" />;
                          case 'completed':
                            return <Chip label="Completed" color="success" size="small" />;
                          default:
                            return <Chip label="Unknown" color="default" size="small" />;
                        }
                      })()}
                    </TableCell>
                    <TableCell>
                      {item.currentPassageIndex} / {item.passages.length}
                    </TableCell>
                    <TableCell>
                      {new Date(item.createdAt).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      })}
                    </TableCell>
                    <TableCell>
                      <IconButton
                        onClick={() => handleQRCodeClick(item.linkId, true)}
                        sx={{
                          color: '#f57c00',
                          '&:hover': {
                            backgroundColor: 'rgba(245, 124, 0, 0.1)',
                            transform: 'scale(1.1)'
                          },
                          transition: 'all 0.2s ease'
                        }}
                      >
                        <QrCodeIcon fontSize="medium" />
                      </IconButton>
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="outlined"
                        startIcon={<ContentCopyIcon />}
                        onClick={() => navigator.clipboard.writeText(`${window.location.origin}/sequence/${item.linkId}/start`)}
                        sx={{ borderRadius: 2 }}
                      >
                        Copy Link
                      </Button>
                    </TableCell>
                    <TableCell>
                      {item.completedPassages && item.completedPassages.length > 0 ? (
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={() => navigate(`/sequence/${item.linkId}/report`)}
                          sx={{ borderRadius: 2 }}
                        >
                          View Sequence Report
                        </Button>
                      ) : (
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={() => window.open(`/sequence/${item.linkId}/start`, '_blank')}
                          sx={{ borderRadius: 2 }}
                        >
                          {item.status === 'created' ? 'Start Sequence' : 'Open Sequence'}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
        </>
      )}

      {/* Create Test Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={{
        sx: { borderRadius: 4, overflow: 'hidden', background: 'linear-gradient(135deg, #e0f7fa 0%, #fffde7 100%)' }
      }}>
        {selectedPassage && (
          <Box sx={{ width: '100%', height: 180, position: 'relative', background: '#e0f7fa' }}>
            <img
              src={getPassageImage(selectedPassage)}
              alt={selectedPassage.title}
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
            <Box sx={{ position: 'absolute', left: 0, right: 0, bottom: 0, p: 2, background: 'linear-gradient(0deg, rgba(0,0,0,0.35) 60%, rgba(0,0,0,0.0) 100%)' }}>
              <Typography variant="h5" fontWeight={700} color="#fff" textShadow="0 2px 8px rgba(0,0,0,0.25)">{selectedPassage.title}</Typography>
            </Box>
          </Box>
        )}
        <DialogTitle sx={{ textAlign: 'center', fontWeight: 700, fontSize: 26, mt: 2, mb: 1, letterSpacing: 1, color: '#4fc3f7', background: 'transparent' }}>
          Assign Passage to Student
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 3 }}>
          <Typography mb={2} color="text.secondary" fontSize={18}>
            Enter the child's name to personalize this reading test.
          </Typography>
          <TextField
            autoFocus
            label="Child's Name"
            type="text"
            variant="outlined"
            fullWidth
            value={childName}
            onChange={e => setChildName(e.target.value)}
            sx={{
              maxWidth: 400,
              background: '#fff',
              borderRadius: 2,
              boxShadow: '0 2px 8px rgba(79,195,247,0.08)',
              input: { fontSize: 20, fontWeight: 600, letterSpacing: 1 },
            }}
            inputProps={{ style: { textAlign: 'center' } }}
          />
          
          <Box sx={{ mt: 3, alignSelf: 'stretch', maxWidth: 400, textAlign: 'center' }}>
            <Typography 
              variant="body2" 
              sx={{ 
                color: 'text.secondary', 
                fontSize: 14,
                fontStyle: 'italic',
                backgroundColor: 'rgba(79, 195, 247, 0.08)',
                padding: '8px 16px',
                borderRadius: 2,
                border: '1px solid rgba(79, 195, 247, 0.2)'
              }}
            >
              ✨ Graded using Whisper AI for fast and accurate scoring
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
          <Button onClick={() => setDialogOpen(false)} color="secondary" sx={{ fontWeight: 'bold', borderRadius: 2, px: 4, py: 1.5 }}>Cancel</Button>
          <Button onClick={handleCreateTest} variant="contained" color="primary" disabled={!childName.trim() || creating} sx={{ fontWeight: 'bold', borderRadius: 2, px: 4, py: 1.5, background: 'linear-gradient(90deg, #4fc3f7 0%, #81c784 100%)' }}>Create</Button>
        </DialogActions>
      </Dialog>

      {/* Create Passage Dialog */}
      <Dialog open={createPassageOpen} onClose={() => setCreatePassageOpen(false)} maxWidth="lg" fullWidth PaperProps={{
        sx: { borderRadius: 4, overflow: 'hidden', background: 'linear-gradient(135deg, #e0f7fa 0%, #fffde7 100%)', minHeight: '80vh' }
      }}>
        <DialogTitle sx={{ textAlign: 'center', fontWeight: 800, fontSize: 32, mt: 2, mb: 1, letterSpacing: 1, color: '#81c784', background: 'transparent' }}>
          Create New Passage
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'row', gap: 4, py: 3, minHeight: '600px' }}>
          {/* Left Panel - Form Fields */}
          <Box sx={{ flex: '0 0 400px', display: 'flex', flexDirection: 'column', gap: 3 }}>
            <TextField
              label="Passage Name"
              type="text"
              variant="outlined"
              fullWidth
              value={newPassageName}
              onChange={e => setNewPassageName(e.target.value)}
              sx={{ background: '#fff', borderRadius: 2, input: { fontSize: 18, fontWeight: 600 } }}
            />

            <FormControl sx={{ width: '100%' }}>
              <FormLabel sx={{ fontWeight: 700, color: '#81c784', mb: 2, fontSize: 16 }}>Passage Type</FormLabel>
              <RadioGroup
                value={newPassageType}
                onChange={(e) => setNewPassageType(e.target.value)}
                sx={{ gap: 2 }}
              >
                <FormControlLabel 
                  value="assessment" 
                  control={<Radio />} 
                  label={
                    <Box>
                      <Typography fontWeight={600} fontSize={16}>📋 Assessment</Typography>
                      <Typography variant="caption" color="text.secondary" fontSize={13}>
                        Text is broken down line-by-line. Students read one line at a time with Next button.
                      </Typography>
                    </Box>
                  }
                  sx={{ 
                    border: '2px solid #e0e0e0',
                    borderRadius: 2,
                    p: 2.5,
                    m: 0,
                    bgcolor: newPassageType === 'assessment' ? 'rgba(33, 150, 243, 0.1)' : 'transparent',
                    borderColor: newPassageType === 'assessment' ? '#2196f3' : '#e0e0e0'
                  }}
                />
                <FormControlLabel 
                  value="story" 
                  control={<Radio />} 
                  label={
                    <Box>
                      <Typography fontWeight={600} fontSize={16}>📚 Story</Typography>
                      <Typography variant="caption" color="text.secondary" fontSize={13}>
                        Text is broken down paragraph-by-paragraph. Students read one paragraph at a time.
                      </Typography>
                    </Box>
                  }
                  sx={{ 
                    border: '2px solid #e0e0e0',
                    borderRadius: 2,
                    p: 2.5,
                    m: 0,
                    bgcolor: newPassageType === 'story' ? 'rgba(156, 39, 176, 0.1)' : 'transparent',
                    borderColor: newPassageType === 'story' ? '#9c27b0' : '#e0e0e0'
                  }}
                />
              </RadioGroup>
            </FormControl>

            <FormControl fullWidth>
              <Typography variant="subtitle2" fontWeight={600} color="text.primary" mb={1}>
                Grade Level *
              </Typography>
              <Select
                value={newPassageGradeLevel}
                onChange={(e) => setNewPassageGradeLevel(e.target.value)}
                displayEmpty
                sx={{ background: '#fff', borderRadius: 2 }}
              >
                <MenuItem value="" disabled>Select Grade Level</MenuItem>
                <MenuItem value="K">Kindergarten</MenuItem>
                <MenuItem value="1">1st Grade</MenuItem>
                <MenuItem value="2">2nd Grade</MenuItem>
                <MenuItem value="3">3rd Grade</MenuItem>
                <MenuItem value="4">4th Grade</MenuItem>
                <MenuItem value="5">5th Grade</MenuItem>
                <MenuItem value="6">6th Grade</MenuItem>
                <MenuItem value="7">7th Grade</MenuItem>
                <MenuItem value="8">8th Grade</MenuItem>
                <MenuItem value="9">9th Grade</MenuItem>
                <MenuItem value="10">10th Grade</MenuItem>
                <MenuItem value="11">11th Grade</MenuItem>
                <MenuItem value="12">12th Grade</MenuItem>
              </Select>
            </FormControl>

            {newPassageType === 'story' && (
              <FormControl fullWidth>
                <Typography variant="subtitle2" fontWeight={600} color="text.primary" mb={1}>
                  Type of Story *
                </Typography>
                <Select
                  value={newPassageStoryType}
                  onChange={(e) => setNewPassageStoryType(e.target.value)}
                  displayEmpty
                  sx={{ background: '#fff', borderRadius: 2 }}
                >
                  <MenuItem value="" disabled>Select Story Type</MenuItem>
                  <MenuItem value="Controlled Decodable">Controlled Decodable</MenuItem>
                  <MenuItem value="Expository">Expository</MenuItem>
                  <MenuItem value="Fiction">Fiction</MenuItem>
                </Select>
              </FormControl>
            )}

            <FormControl fullWidth>
              <Typography variant="subtitle2" fontWeight={600} color="text.primary" mb={1}>
                Source *
              </Typography>
              <Select
                value={newPassageSource}
                onChange={(e) => setNewPassageSource(e.target.value)}
                displayEmpty
                sx={{ background: '#fff', borderRadius: 2 }}
              >
                <MenuItem value="" disabled>Select Source</MenuItem>
                <MenuItem value="RGR Blast">RGR Blast</MenuItem>
                <MenuItem value="RGR Countdown">RGR Countdown</MenuItem>
                <MenuItem value="RGR HD Word">RGR HD Word</MenuItem>
                <MenuItem value="RGR Boost">RGR Boost</MenuItem>
                <MenuItem value="DIBELS">DIBELS</MenuItem>
                <MenuItem value="RGR Surveys">RGR Surveys</MenuItem>
                <MenuItem value="ReadWorks">ReadWorks</MenuItem>
                <MenuItem value="Other">Other</MenuItem>
              </Select>
            </FormControl>

            <Box
              onDrop={handleImageDrop}
              onDragOver={e => e.preventDefault()}
              sx={{
                width: '100%',
                height: 200,
                border: '2px dashed #81c784',
                borderRadius: 3,
                background: '#f9fafc',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
                cursor: 'pointer',
                position: 'relative',
                transition: 'border 0.2s',
                '&:hover': { border: '2px solid #4fc3f7' }
              }}
              onClick={() => document.getElementById('passage-image-input').click()}
            >
              {newPassageImagePreview ? (
                <>
                  <img src={newPassageImagePreview} alt="Preview" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 8 }} />
                  <Button size="small" color="error" onClick={e => { e.stopPropagation(); handleImageRemove(); }} sx={{ position: 'absolute', top: 8, right: 8, borderRadius: 2, fontWeight: 700 }}>Remove</Button>
                </>
              ) : (
                <>
                  <AddPhotoAlternateIcon sx={{ fontSize: 48, color: '#81c784', mb: 1 }} />
                  <Typography color="#81c784" fontWeight={700} fontSize={18}>Upload Cover Image</Typography>
                  <Typography color="#aaa" fontSize={14}>(Optional - Drag & drop or click)</Typography>
                </>
              )}
              <input
                id="passage-image-input"
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={handleImageChange}
              />
            </Box>
          </Box>

          {/* Right Panel - Text Editor with Preview */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
              <Typography variant="h6" fontWeight={700} color="#2196f3">
                Passage Content
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                {newPassageType && (
                  <Chip 
                    label={newPassageType === 'story' ? 'Will break by paragraphs' : 'Will break by lines'}
                    color={newPassageType === 'story' ? 'secondary' : 'primary'}
                    size="small"
                    sx={{ fontSize: '0.75rem', fontWeight: 600 }}
                  />
                )}
                {newPassageText && (
                  <Chip 
                    label={`${calculateWordCount(newPassageText)} words`}
                    color="default"
                    size="small"
                    sx={{ fontSize: '0.75rem', fontWeight: 600 }}
                  />
                )}
                <Chip 
                  label="Lexile calculated on creation"
                  color="info"
                  size="small"
                  sx={{ fontSize: '0.75rem', fontWeight: 600 }}
                />
              </Box>
            </Box>
            
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              <TextField
                label={`Enter your ${newPassageType === 'story' ? 'story' : 'assessment'} text here`}
                multiline
                fullWidth
                minRows={20}
                value={newPassageText}
                onChange={e => {
                  const text = e.target.value;
                  setNewPassageText(text);
                  // No auto-calculation, user will see lexile calculated when they submit
                  console.log('📝 New passage text changed, will calculate on create');
                }}
                sx={{ 
                  flex: 1,
                  background: '#fff', 
                  borderRadius: 2,
                  '& .MuiInputBase-root': {
                    height: '100%',
                    alignItems: 'flex-start'
                  },
                  '& .MuiInputBase-input': {
                    fontSize: 16,
                    lineHeight: 1.6,
                    fontFamily: 'Georgia, serif',
                    height: '100% !important',
                    overflow: 'auto !important'
                  }
                }}
              />
              
              {/* Live Preview of how text will be broken down */}
              {newPassageText && (
                <Box sx={{ 
                  mt: 2, 
                  p: 2, 
                  background: 'rgba(255,255,255,0.9)', 
                  borderRadius: 2, 
                  border: '1px solid rgba(0,0,0,0.1)',
                  maxHeight: '200px',
                  overflow: 'auto'
                }}>
                  <Typography variant="subtitle2" fontWeight={600} color="#666" mb={1}>
                    📋 Preview: How students will see this {newPassageType === 'story' ? 'story' : 'assessment'}
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {newPassageType === 'story' 
                      ? newPassageText.split('\n\n').filter(p => p.trim()).map((paragraph, index) => (
                          <Box key={index} sx={{ 
                            p: 1.5, 
                            background: '#f8f9fa', 
                            borderRadius: 1, 
                            border: '1px solid #e0e0e0',
                            borderLeft: '4px solid #9c27b0'
                          }}>
                            <Typography variant="caption" color="#9c27b0" fontWeight={600}>
                              Paragraph {index + 1}
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 0.5, lineHeight: 1.4 }}>
                              {paragraph.trim()}
                            </Typography>
                          </Box>
                        ))
                      : newPassageText.split('\n').filter(line => line.trim()).map((line, index) => (
                          <Box key={index} sx={{ 
                            p: 1, 
                            background: '#f8f9fa', 
                            borderRadius: 1, 
                            border: '1px solid #e0e0e0',
                            borderLeft: '4px solid #2196f3'
                          }}>
                            <Typography variant="caption" color="#2196f3" fontWeight={600}>
                              Line {index + 1}
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 0.5, lineHeight: 1.4 }}>
                              {line.trim()}
                            </Typography>
                          </Box>
                        ))
                    }
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    {newPassageType === 'story'
                      ? '💡 Tip: Separate paragraphs with double line breaks for best results'
                      : '💡 Tip: Each line break creates a new reading segment'
                    }
                  </Typography>
                </Box>
              )}

              {/* Message about video uploads */}
              {newPassageText && (
                <Box sx={{
                  mt: 2,
                  p: 2,
                  background: 'rgba(156, 39, 176, 0.05)',
                  borderRadius: 2,
                  border: '1px dashed rgba(156, 39, 176, 0.3)',
                  textAlign: 'center'
                }}>
                  <Typography variant="body2" color="#9c27b0" fontWeight={500}>
                    Video instructions can be added after creating the passage.
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Create the passage first, then edit it to add videos before each {newPassageType === 'story' ? 'paragraph' : 'line'}.
                  </Typography>
                </Box>
              )}

            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3, borderTop: '1px solid rgba(0,0,0,0.1)', pt: 2 }}>
          <Button onClick={() => setCreatePassageOpen(false)} color="secondary" variant="outlined" sx={{ fontWeight: 'bold', borderRadius: 2, px: 6, py: 1.5, fontSize: 16 }}>Cancel</Button>
          <Button onClick={handleCreatePassage} variant="contained" color="primary" disabled={!newPassageName.trim() || !newPassageText.trim() || !newPassageGradeLevel || !newPassageSource || (newPassageType === 'story' && !newPassageStoryType) || uploadingPassage} sx={{ fontWeight: 'bold', borderRadius: 2, px: 6, py: 1.5, fontSize: 16, background: 'linear-gradient(90deg, #4fc3f7 0%, #81c784 100%)' }}>
            {uploadingPassage ? '⏳ Creating...' : '✨ Create Passage'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Sequence Assignment Dialog */}
      <Dialog 
        open={sequenceDialogOpen} 
        onClose={() => setSequenceDialogOpen(false)} 
        maxWidth="lg" 
        fullWidth 
        PaperProps={{
          sx: { 
            borderRadius: 4, 
            overflow: 'hidden', 
            background: 'linear-gradient(135deg, #fff8e1 0%, #f3e5ab 100%)',
            minHeight: '70vh',
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle sx={{ textAlign: 'center', fontWeight: 700, fontSize: 26, mt: 2, mb: 1, letterSpacing: 1, color: '#f57c00', background: 'transparent' }}>
          Create Passage Sequence
        </DialogTitle>
        <DialogContent sx={{ py: 3 }}>
          <Stepper activeStep={sequenceStep} sx={{ mb: 4 }}>
            <Step>
              <StepLabel>Select Passages</StepLabel>
            </Step>
            <Step>
              <StepLabel>Assign to Student</StepLabel>
            </Step>
          </Stepper>

          {sequenceStep === 0 && (
            <Box>
              <Typography variant="h6" mb={4} textAlign="center" color="text.secondary">
                Select the passages for your sequence. They will be attempted in the order shown.
              </Typography>
              <Box 
                sx={{ 
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 3,
                  justifyContent: 'center',
                  alignItems: 'stretch'
                }}
              >
                {passages.map((passage, index) => {
                  const sequenceNumber = getPassageSequenceNumber(passage._id);
                  const isSelected = sequenceNumber !== null;
                  return (
                    <Card 
                      key={passage._id}
                      sx={{ 
                        width: 280,
                        maxWidth: '100%',
                        borderRadius: 3, 
                        cursor: 'pointer',
                        border: isSelected ? '3px solid #f57c00' : '2px solid rgba(0,0,0,0.08)',
                        position: 'relative',
                        transform: isSelected ? 'scale(0.98)' : 'scale(1)',
                        transition: 'all 0.3s ease',
                        boxShadow: isSelected ? '0 8px 24px rgba(245,124,0,0.3)' : '0 4px 12px rgba(0,0,0,0.08)',
                        display: 'flex',
                        flexDirection: 'column',
                        '&:hover': { 
                          transform: isSelected ? 'scale(0.99)' : 'scale(1.02)',
                          boxShadow: '0 8px 24px rgba(0,0,0,0.15)'
                        }
                      }}
                      onClick={() => handlePassageToggle(passage)}
                    >
                      {isSelected && (
                        <Chip
                          label={sequenceNumber}
                          color="warning"
                          sx={{ 
                            position: 'absolute', 
                            top: 12, 
                            right: 12, 
                            zIndex: 2, 
                            fontWeight: 'bold',
                            fontSize: 16,
                            minWidth: 36,
                            height: 36,
                            boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                          }}
                        />
                      )}
                      <CardMedia
                        component="img"
                        height="140"
                        image={getPassageImage(passage)}
                        alt={passage.title}
                        sx={{ objectFit: 'cover', borderTopLeftRadius: 12, borderTopRightRadius: 12 }}
                      />
                      <CardContent sx={{ p: 2.5, flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                        <Box>
                          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                            <Typography variant="subtitle1" fontWeight={700} fontSize={16} lineHeight={1.3}>
                              {passage.title}
                            </Typography>
                            <Chip 
                              label={passage.passageType === 'story' ? 'Story' : 'Assessment'}
                              size="small"
                              color={passage.passageType === 'story' ? 'secondary' : 'primary'}
                              sx={{ fontSize: '0.6rem', fontWeight: 600, height: '18px' }}
                            />
                          </Box>
                          <Typography color="text.secondary" fontSize={13} lineHeight={1.4} mb={1}>
                            {getExcerpt(passage.text)}
                          </Typography>
                        </Box>
                        <Box display="flex" gap={0.5} flexWrap="wrap">
                          {passage.gradeLevel && (
                            <Chip 
                              label={`Gr.${passage.gradeLevel}`}
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: '0.55rem', height: '18px' }}
                            />
                          )}
                          {passage.wordCount && (
                            <Chip 
                              label={`${passage.wordCount}w`}
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: '0.55rem', height: '18px' }}
                            />
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  );
                })}
              </Box>
              {selectedPassages.length > 0 && (
                <Box mt={4} textAlign="center" p={2} borderRadius={2} sx={{ bgcolor: 'rgba(76,175,80,0.1)' }}>
                  <Typography variant="body1" color="success.main" fontWeight={700} fontSize={18}>
                    ✓ {selectedPassages.length} passage{selectedPassages.length !== 1 ? 's' : ''} selected for sequence
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {sequenceStep === 1 && (
            <Box display="flex" flexDirection="column" alignItems="center" sx={{ maxWidth: 600, mx: 'auto' }}>
              <Typography variant="h6" mb={2} textAlign="center" color="text.secondary" fontSize={18}>
                {saveSequenceMode ? 'Save this sequence for future use' : 'Assign this sequence to a student'}
              </Typography>
              
              {/* Mode Toggle Buttons */}
              <Box display="flex" gap={2} mb={4} p={1} bgcolor="rgba(0,0,0,0.05)" borderRadius={3}>
                <Button
                  variant={saveSequenceMode ? "outlined" : "contained"}
                  onClick={() => setSaveSequenceMode(false)}
                  sx={{ 
                    fontWeight: 600, 
                    borderRadius: 2,
                    px: 3,
                    background: !saveSequenceMode ? 'linear-gradient(90deg, #4caf50 0%, #66bb6a 100%)' : 'transparent',
                    border: !saveSequenceMode ? 'none' : '1px solid #4caf50',
                    color: !saveSequenceMode ? '#fff' : '#4caf50'
                  }}
                >
                  👤 Assign to Student
                </Button>
                <Button
                  variant={saveSequenceMode ? "contained" : "outlined"}
                  onClick={() => setSaveSequenceMode(true)}
                  sx={{ 
                    fontWeight: 600, 
                    borderRadius: 2,
                    px: 3,
                    background: saveSequenceMode ? 'linear-gradient(90deg, #2196f3 0%, #64b5f6 100%)' : 'transparent',
                    border: saveSequenceMode ? 'none' : '1px solid #2196f3',
                    color: saveSequenceMode ? '#fff' : '#2196f3'
                  }}
                >
                  💾 Save Template
                </Button>
              </Box>
              
              <Box mb={4} width="100%" sx={{ bgcolor: 'rgba(255,255,255,0.8)', borderRadius: 3, p: 3, border: '1px solid rgba(245,124,0,0.2)' }}>
                <Typography variant="h6" fontWeight={700} mb={3} color="#f57c00" textAlign="center">
                  📚 Selected Sequence ({selectedPassages.length} passages)
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {selectedPassages.map((passage, index) => (
                    <Box 
                      key={passage._id} 
                      sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        p: 2, 
                        bgcolor: 'rgba(255,255,255,0.9)', 
                        borderRadius: 2,
                        border: '1px solid rgba(0,0,0,0.08)',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                      }}
                    >
                      <Chip 
                        label={index + 1} 
                        color="warning" 
                        sx={{ mr: 3, fontWeight: 'bold', fontSize: 16, minWidth: 40, height: 32 }}
                      />
                      <Box flexGrow={1}>
                        <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                          <Typography fontWeight={700} fontSize={16} color="text.primary">
                            {passage.title}
                          </Typography>
                          <Chip 
                            label={passage.passageType === 'story' ? 'Story' : 'Assessment'}
                            size="small"
                            color={passage.passageType === 'story' ? 'secondary' : 'primary'}
                            sx={{ fontSize: '0.6rem', fontWeight: 600, height: '18px' }}
                          />
                        </Box>
                        <Typography variant="body2" color="text.secondary" fontSize={13} mb={0.5}>
                          {getExcerpt(passage.text)}
                        </Typography>
                        <Box display="flex" gap={0.5} flexWrap="wrap">
                          {passage.gradeLevel && (
                            <Chip 
                              label={`Grade ${passage.gradeLevel}`}
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: '0.55rem', height: '16px' }}
                            />
                          )}
                          {passage.wordCount && (
                            <Chip 
                              label={`${passage.wordCount} words`}
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: '0.55rem', height: '16px' }}
                            />
                          )}
                          {passage.lexileLevel && (
                            <Chip 
                              label={`Lexile: ${passage.lexileLevel}`}
                              size="small"
                              variant="outlined"
                              color="success"
                              sx={{ fontSize: '0.55rem', height: '16px' }}
                            />
                          )}
                        </Box>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>

              {saveSequenceMode ? (
                // Save Sequence Mode - Title and Description fields
                <Box display="flex" flexDirection="column" alignItems="center" gap={3} width="100%">
                  <TextField
                    autoFocus
                    label="Sequence Title *"
                    type="text"
                    variant="outlined"
                    fullWidth
                    value={sequenceTitle}
                    onChange={e => setSequenceTitle(e.target.value)}
                    sx={{
                      maxWidth: 450,
                      background: '#fff',
                      borderRadius: 3,
                      boxShadow: '0 4px 12px rgba(33,150,243,0.1)',
                      '& .MuiOutlinedInput-root': {
                        fontSize: 18,
                        fontWeight: 600,
                        letterSpacing: 1,
                        '&.Mui-focused': {
                          boxShadow: '0 0 0 3px rgba(33,150,243,0.2)'
                        }
                      },
                    }}
                    inputProps={{ style: { textAlign: 'center' } }}
                    placeholder="e.g., 3rd Grade Reading Assessment Bundle"
                  />
                  
                  <TextField
                    label="Description (Optional)"
                    type="text"
                    variant="outlined"
                    fullWidth
                    multiline
                    rows={3}
                    value={sequenceDescription}
                    onChange={e => setSequenceDescription(e.target.value)}
                    sx={{
                      maxWidth: 450,
                      background: '#fff',
                      borderRadius: 3,
                      boxShadow: '0 4px 12px rgba(33,150,243,0.1)',
                      '& .MuiOutlinedInput-root': {
                        fontSize: 16,
                        '&.Mui-focused': {
                          boxShadow: '0 0 0 3px rgba(33,150,243,0.2)'
                        }
                      },
                    }}
                    placeholder="Add a description to help you remember what this sequence is for..."
                  />
                  
                  <Box sx={{ maxWidth: 450, width: '100%', textAlign: 'center' }}>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: 'text.secondary', 
                        fontSize: 15,
                        fontStyle: 'italic',
                        backgroundColor: 'rgba(33, 150, 243, 0.08)',
                        padding: '12px 20px',
                        borderRadius: 3,
                        border: '1px solid rgba(33, 150, 243, 0.2)',
                        fontWeight: 500
                      }}
                    >
                      💾 Save this sequence as a template to reuse with multiple students
                    </Typography>
                  </Box>
                </Box>
              ) : (
                // Assign to Student Mode - Student name field
                <Box display="flex" flexDirection="column" alignItems="center" width="100%">
                  <TextField
                    autoFocus
                    label="Student's Name"
                    type="text"
                    variant="outlined"
                    fullWidth
                    value={sequenceChildName}
                    onChange={e => setSequenceChildName(e.target.value)}
                    sx={{
                      maxWidth: 450,
                      mb: 4,
                      background: '#fff',
                      borderRadius: 3,
                      boxShadow: '0 4px 12px rgba(76,175,80,0.1)',
                      '& .MuiOutlinedInput-root': {
                        fontSize: 18,
                        fontWeight: 600,
                        letterSpacing: 1,
                        '&.Mui-focused': {
                          boxShadow: '0 0 0 3px rgba(76,175,80,0.2)'
                        }
                      },
                    }}
                    inputProps={{ style: { textAlign: 'center' } }}
                  />
                  
                  <Box sx={{ maxWidth: 450, width: '100%', textAlign: 'center', mt: 2 }}>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: 'text.secondary', 
                        fontSize: 15,
                        fontStyle: 'italic',
                        backgroundColor: 'rgba(76, 175, 80, 0.08)',
                        padding: '12px 20px',
                        borderRadius: 3,
                        border: '1px solid rgba(76, 175, 80, 0.2)',
                        fontWeight: 500
                      }}
                    >
                      ✨ All passages in this sequence will be graded using Whisper AI
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', pb: 4, px: 4, pt: 3, borderTop: '1px solid rgba(0,0,0,0.08)' }}>
          <Button 
            onClick={() => setSequenceDialogOpen(false)} 
            color="secondary" 
            variant="outlined"
            sx={{ 
              fontWeight: 700, 
              borderRadius: 3, 
              px: 5, 
              py: 2, 
              fontSize: 16,
              border: '2px solid rgba(0,0,0,0.2)',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.05)' }
            }}
          >
            Cancel
          </Button>
          <Box display="flex" gap={2}>
            {sequenceStep === 1 && (
              <Button 
                onClick={handleSequenceBack} 
                color="secondary" 
                variant="outlined"
                sx={{ 
                  fontWeight: 700, 
                  borderRadius: 3, 
                  px: 5, 
                  py: 2, 
                  fontSize: 16,
                  border: '2px solid #f57c00',
                  color: '#f57c00',
                  '&:hover': { bgcolor: 'rgba(245,124,0,0.1)' }
                }}
              >
                ← Back
              </Button>
            )}
            {sequenceStep === 0 ? (
              <Button 
                onClick={handleSequenceNext} 
                variant="contained" 
                disabled={selectedPassages.length === 0} 
                sx={{ 
                  fontWeight: 700, 
                  borderRadius: 3, 
                  px: 6, 
                  py: 2, 
                  fontSize: 16,
                  background: selectedPassages.length === 0 ? 'rgba(0,0,0,0.12)' : 'linear-gradient(90deg, #f57c00 0%, #ff9800 100%)',
                  boxShadow: selectedPassages.length === 0 ? 'none' : '0 4px 12px rgba(245,124,0,0.3)',
                  '&:hover': { 
                    background: selectedPassages.length === 0 ? 'rgba(0,0,0,0.12)' : 'linear-gradient(90deg, #e65100 0%, #f57c00 100%)'
                  }
                }}
              >
                Next Step →
              </Button>
            ) : (
              <Button 
                onClick={saveSequenceMode ? handleSaveSequence : handleCreateSequence} 
                variant="contained" 
                disabled={
                  saveSequenceMode 
                    ? (!sequenceTitle.trim() || savingSequence)
                    : (!sequenceChildName.trim() || creatingSequence)
                } 
                sx={{ 
                  fontWeight: 700, 
                  borderRadius: 3, 
                  px: 6, 
                  py: 2, 
                  fontSize: 16,
                  background: (
                    (saveSequenceMode && (!sequenceTitle.trim() || savingSequence)) ||
                    (!saveSequenceMode && (!sequenceChildName.trim() || creatingSequence))
                  ) ? 'rgba(0,0,0,0.12)' : saveSequenceMode 
                    ? 'linear-gradient(90deg, #2196f3 0%, #64b5f6 100%)'
                    : 'linear-gradient(90deg, #4caf50 0%, #66bb6a 100%)',
                  boxShadow: (
                    (saveSequenceMode && (!sequenceTitle.trim() || savingSequence)) ||
                    (!saveSequenceMode && (!sequenceChildName.trim() || creatingSequence))
                  ) ? 'none' : saveSequenceMode 
                    ? '0 4px 12px rgba(33,150,243,0.3)'
                    : '0 4px 12px rgba(76,175,80,0.3)',
                  '&:hover': { 
                    background: (
                      (saveSequenceMode && (!sequenceTitle.trim() || savingSequence)) ||
                      (!saveSequenceMode && (!sequenceChildName.trim() || creatingSequence))
                    ) ? 'rgba(0,0,0,0.12)' : saveSequenceMode
                      ? 'linear-gradient(90deg, #1976d2 0%, #2196f3 100%)'
                      : 'linear-gradient(90deg, #2e7d32 0%, #4caf50 100%)'
                  }
                }}
              >
                {saveSequenceMode ? (
                  savingSequence ? '⏳ Saving...' : '💾 Save Sequence'
                ) : (
                  creatingSequence ? '⏳ Creating...' : '✓ Assign Sequence'
                )}
              </Button>
            )}
          </Box>
        </DialogActions>
      </Dialog>

      {/* QR Code Modal */}
      <Dialog
        open={qrModalOpen}
        onClose={handleQRModalClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            overflow: 'hidden',
            background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
          }
        }}
        BackdropProps={{
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            backdropFilter: 'blur(5px)'
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          p: 3,
          background: 'linear-gradient(90deg, #4fc3f7 0%, #81c784 100%)',
          color: 'white'
        }}>
          <Box display="flex" alignItems="center" gap={2}>
            <QrCodeIcon fontSize="large" />
            <Typography variant="h5" fontWeight={700}>
              QR Code
            </Typography>
          </Box>
          <IconButton 
            onClick={handleQRModalClose} 
            sx={{ 
              color: 'white',
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.2)'
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 4, textAlign: 'center' }}>
          {qrCodeImage && (
            <Box display="flex" flexDirection="column" alignItems="center" gap={3}>
              <Box
                sx={{
                  p: 3,
                  backgroundColor: 'white',
                  borderRadius: 3,
                  boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                  border: '1px solid rgba(79, 195, 247, 0.2)'
                }}
              >
                <img 
                  src={qrCodeImage} 
                  alt="QR Code" 
                  style={{ 
                    width: '300px', 
                    height: '300px',
                    borderRadius: '8px'
                  }} 
                />
              </Box>
              <Box>
                <Typography 
                  variant="h6" 
                  fontWeight={600}
                  color="text.primary"
                  mb={2}
                >
                  Scan to access test
                </Typography>
                <Typography 
                  variant="body2" 
                  color="text.secondary"
                  sx={{
                    backgroundColor: 'rgba(79, 195, 247, 0.1)',
                    padding: '8px 16px',
                    borderRadius: 2,
                    fontSize: '12px',
                    fontFamily: 'monospace',
                    wordBreak: 'break-all',
                    border: '1px solid rgba(79, 195, 247, 0.3)'
                  }}
                >
                  {qrCodeUrl}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ 
          justifyContent: 'center', 
          p: 3, 
          borderTop: '1px solid rgba(0,0,0,0.1)' 
        }}>
          <Button 
            onClick={handleQRModalClose} 
            variant="contained"
            sx={{ 
              fontWeight: 700,
              borderRadius: 3,
              px: 4,
              py: 1.5,
              background: 'linear-gradient(90deg, #4fc3f7 0%, #81c784 100%)',
              boxShadow: '0 4px 16px rgba(79,195,247,0.3)',
              '&:hover': {
                background: 'linear-gradient(90deg, #29b6f6 0%, #66bb6a 100%)',
                transform: 'translateY(-1px)',
                boxShadow: '0 6px 20px rgba(79,195,247,0.4)'
              }
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Passage Dialog */}
      <Dialog open={editPassageOpen} onClose={() => setEditPassageOpen(false)} maxWidth="lg" fullWidth PaperProps={{
        sx: { borderRadius: 4, overflow: 'hidden', background: 'linear-gradient(135deg, #e0f7fa 0%, #fffde7 100%)', minHeight: '80vh' }
      }}>
        <DialogTitle sx={{ textAlign: 'center', fontWeight: 800, fontSize: 32, mt: 2, mb: 1, letterSpacing: 1, color: '#81c784', background: 'transparent' }}>
          Edit Passage
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'row', gap: 4, py: 3, minHeight: '600px' }}>
          {/* Left Panel - Form Fields */}
          <Box sx={{ flex: '0 0 400px', display: 'flex', flexDirection: 'column', gap: 3 }}>
            <TextField
              label="Passage Name"
              type="text"
              variant="outlined"
              fullWidth
              value={editPassageName}
              onChange={e => setEditPassageName(e.target.value)}
              sx={{ background: '#fff', borderRadius: 2, input: { fontSize: 18, fontWeight: 600 } }}
            />

            <FormControl sx={{ width: '100%' }}>
              <FormLabel sx={{ fontWeight: 700, color: '#81c784', mb: 2, fontSize: 16 }}>Passage Type</FormLabel>
              <RadioGroup
                value={editPassageType}
                onChange={(e) => setEditPassageType(e.target.value)}
                sx={{ gap: 2 }}
              >
                <FormControlLabel 
                  value="assessment" 
                  control={<Radio />} 
                  label={
                    <Box>
                      <Typography fontWeight={600} fontSize={16}>📋 Assessment</Typography>
                      <Typography variant="caption" color="text.secondary" fontSize={13}>
                        Text is broken down line-by-line. Students read one line at a time with Next button.
                      </Typography>
                    </Box>
                  }
                  sx={{ 
                    border: '2px solid #e0e0e0',
                    borderRadius: 2,
                    p: 2.5,
                    m: 0,
                    bgcolor: editPassageType === 'assessment' ? 'rgba(33, 150, 243, 0.1)' : 'transparent',
                    borderColor: editPassageType === 'assessment' ? '#2196f3' : '#e0e0e0'
                  }}
                />
                <FormControlLabel 
                  value="story" 
                  control={<Radio />} 
                  label={
                    <Box>
                      <Typography fontWeight={600} fontSize={16}>📚 Story</Typography>
                      <Typography variant="caption" color="text.secondary" fontSize={13}>
                        Text is broken down paragraph-by-paragraph. Students read one paragraph at a time.
                      </Typography>
                    </Box>
                  }
                  sx={{ 
                    border: '2px solid #e0e0e0',
                    borderRadius: 2,
                    p: 2.5,
                    m: 0,
                    bgcolor: editPassageType === 'story' ? 'rgba(156, 39, 176, 0.1)' : 'transparent',
                    borderColor: editPassageType === 'story' ? '#9c27b0' : '#e0e0e0'
                  }}
                />
              </RadioGroup>
            </FormControl>

            <FormControl fullWidth>
              <Typography variant="subtitle2" fontWeight={600} color="text.primary" mb={1}>
                Grade Level *
              </Typography>
              <Select
                value={editPassageGradeLevel}
                onChange={(e) => setEditPassageGradeLevel(e.target.value)}
                displayEmpty
                sx={{ background: '#fff', borderRadius: 2 }}
              >
                <MenuItem value="" disabled>Select Grade Level</MenuItem>
                <MenuItem value="K">Kindergarten</MenuItem>
                <MenuItem value="1">1st Grade</MenuItem>
                <MenuItem value="2">2nd Grade</MenuItem>
                <MenuItem value="3">3rd Grade</MenuItem>
                <MenuItem value="4">4th Grade</MenuItem>
                <MenuItem value="5">5th Grade</MenuItem>
                <MenuItem value="6">6th Grade</MenuItem>
                <MenuItem value="7">7th Grade</MenuItem>
                <MenuItem value="8">8th Grade</MenuItem>
                <MenuItem value="9">9th Grade</MenuItem>
                <MenuItem value="10">10th Grade</MenuItem>
                <MenuItem value="11">11th Grade</MenuItem>
                <MenuItem value="12">12th Grade</MenuItem>
              </Select>
            </FormControl>

            {editPassageType === 'story' && (
              <FormControl fullWidth>
                <Typography variant="subtitle2" fontWeight={600} color="text.primary" mb={1}>
                  Type of Story *
                </Typography>
                <Select
                  value={editPassageStoryType}
                  onChange={(e) => setEditPassageStoryType(e.target.value)}
                  displayEmpty
                  sx={{ background: '#fff', borderRadius: 2 }}
                >
                  <MenuItem value="" disabled>Select Story Type</MenuItem>
                  <MenuItem value="Controlled Decodable">Controlled Decodable</MenuItem>
                  <MenuItem value="Expository">Expository</MenuItem>
                  <MenuItem value="Fiction">Fiction</MenuItem>
                </Select>
              </FormControl>
            )}

            <FormControl fullWidth>
              <Typography variant="subtitle2" fontWeight={600} color="text.primary" mb={1}>
                Source *
              </Typography>
              <Select
                value={editPassageSource}
                onChange={(e) => setEditPassageSource(e.target.value)}
                displayEmpty
                sx={{ background: '#fff', borderRadius: 2 }}
              >
                <MenuItem value="" disabled>Select Source</MenuItem>
                <MenuItem value="RGR Blast">RGR Blast</MenuItem>
                <MenuItem value="RGR Countdown">RGR Countdown</MenuItem>
                <MenuItem value="RGR HD Word">RGR HD Word</MenuItem>
                <MenuItem value="RGR Boost">RGR Boost</MenuItem>
                <MenuItem value="DIBELS">DIBELS</MenuItem>
                <MenuItem value="RGR Surveys">RGR Surveys</MenuItem>
                <MenuItem value="ReadWorks">ReadWorks</MenuItem>
                <MenuItem value="Other">Other</MenuItem>
              </Select>
            </FormControl>

            <Box
              onDrop={(e) => {
                e.preventDefault();
                const file = e.dataTransfer.files[0];
                if (file) {
                  setEditPassageImage(file);
                  setEditPassageImagePreview(URL.createObjectURL(file));
                }
              }}
              onDragOver={e => e.preventDefault()}
              sx={{
                width: '100%',
                height: 200,
                border: '2px dashed #81c784',
                borderRadius: 3,
                background: '#f9fafc',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
                cursor: 'pointer',
                position: 'relative',
                transition: 'border 0.2s',
                '&:hover': { border: '2px solid #4fc3f7' }
              }}
              onClick={() => document.getElementById('edit-passage-image-input').click()}
            >
              {editPassageImagePreview ? (
                <>
                  <img src={editPassageImagePreview} alt="Preview" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 8 }} />
                  <Button size="small" color="error" onClick={e => { e.stopPropagation(); handleEditImageRemove(); }} sx={{ position: 'absolute', top: 8, right: 8, borderRadius: 2, fontWeight: 700 }}>Remove</Button>
                </>
              ) : (
                <>
                  <AddPhotoAlternateIcon sx={{ fontSize: 48, color: '#81c784', mb: 1 }} />
                  <Typography color="#81c784" fontWeight={700} fontSize={18}>Upload Cover Image</Typography>
                  <Typography color="#aaa" fontSize={14}>(Optional - Drag & drop or click)</Typography>
                </>
              )}
              <input
                id="edit-passage-image-input"
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={handleEditImageChange}
              />
            </Box>
          </Box>

          {/* Right Panel - Text Editor with Preview */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
              <Typography variant="h6" fontWeight={700} color="#2196f3">
                Passage Content
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                {editPassageType && (
                  <Chip 
                    label={editPassageType === 'story' ? 'Will break by paragraphs' : 'Will break by lines'}
                    color={editPassageType === 'story' ? 'secondary' : 'primary'}
                    size="small"
                    sx={{ fontSize: '0.75rem', fontWeight: 600 }}
                  />
                )}
                {editPassageText && (
                  <Chip 
                    label={`${calculateWordCount(editPassageText)} words`}
                    color="default"
                    size="small"
                    sx={{ fontSize: '0.75rem', fontWeight: 600 }}
                  />
                )}
                {editPassageLexileLevel && (
                  <Chip 
                    label={`Lexile: ${editPassageLexileLevel}`}
                    color="success"
                    size="small"
                    sx={{ fontSize: '0.75rem', fontWeight: 600 }}
                  />
                )}
                {calculatingLexile && (
                  <Chip 
                    label="calculating Lexile..."
                    color="info"
                    size="small"
                    sx={{ fontSize: '0.75rem', fontWeight: 600 }}
                  />
                )}
              </Box>
            </Box>
            
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              <TextField
                label={`Enter your ${editPassageType === 'story' ? 'story' : 'assessment'} text here`}
                multiline
                fullWidth
                minRows={20}
                value={editPassageText}
                onChange={e => {
                  const text = e.target.value;
                  setEditPassageText(text);
                  
                  // Track if text has changed from original - THAT'S IT!
                  const hasTextChanged = text !== originalEditText;
                  setEditTextChanged(hasTextChanged);
                  
                  // Set flag for lexile calculation (but don't calculate yet)
                  setLexileNeedsCalculation(hasTextChanged && text.trim().length > 0);
                  
                  // If text is back to original, restore original lexile level
                  if (!hasTextChanged) {
                    setEditPassageLexileLevel(passageToEdit?.lexileLevel || '');
                  }
                  
                  console.log('📝 Text changed:', hasTextChanged, 'Will calculate lexile on update:', hasTextChanged && text.trim().length > 0);
                }}
                sx={{ 
                  flex: 1,
                  background: '#fff', 
                  borderRadius: 2,
                  '& .MuiInputBase-root': {
                    height: '100%',
                    alignItems: 'flex-start'
                  },
                  '& .MuiInputBase-input': {
                    fontSize: 16,
                    lineHeight: 1.6,
                    fontFamily: 'Georgia, serif',
                    height: '100% !important',
                    overflow: 'auto !important'
                  }
                }}
              />
              
              {/* Live Preview of how text will be broken down */}
              {editPassageText && (
                <Box sx={{
                  mt: 2,
                  p: 2,
                  background: 'rgba(255,255,255,0.9)',
                  borderRadius: 2,
                  border: '1px solid rgba(0,0,0,0.1)',
                  maxHeight: '200px',
                  overflow: 'auto'
                }}>
                  <Typography variant="subtitle2" fontWeight={600} color="#666" mb={1}>
                    📋 Preview: How students will see this {editPassageType === 'story' ? 'story' : 'assessment'}
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {editPassageType === 'story'
                      ? editPassageText.split('\n\n').filter(p => p.trim()).map((paragraph, index) => (
                          <Box key={index} sx={{
                            p: 1.5,
                            background: '#f8f9fa',
                            borderRadius: 1,
                            border: '1px solid #e0e0e0',
                            borderLeft: '4px solid #9c27b0'
                          }}>
                            <Typography variant="caption" color="#9c27b0" fontWeight={600}>
                              Paragraph {index + 1}
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 0.5, lineHeight: 1.4 }}>
                              {paragraph.trim()}
                            </Typography>
                          </Box>
                        ))
                      : editPassageText.split('\n').filter(line => line.trim()).map((line, index) => (
                          <Box key={index} sx={{
                            p: 1,
                            background: '#f8f9fa',
                            borderRadius: 1,
                            border: '1px solid #e0e0e0',
                            borderLeft: '4px solid #2196f3'
                          }}>
                            <Typography variant="caption" color="#2196f3" fontWeight={600}>
                              Line {index + 1}
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 0.5, lineHeight: 1.4 }}>
                              {line.trim()}
                            </Typography>
                          </Box>
                        ))
                    }
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    {editPassageType === 'story'
                      ? '💡 Tip: Separate paragraphs with double line breaks for best results'
                      : '💡 Tip: Each line break creates a new reading segment'
                    }
                  </Typography>
                </Box>
              )}

              {/* Hero Video Manager - plays before reading starts */}
              {passageToEdit && (
                <HeroVideoManager
                  passageId={passageToEdit._id}
                  heroVideo={editHeroVideo}
                  onHeroVideoChange={setEditHeroVideo}
                  disabled={updatingPassage}
                />
              )}

              {/* Line Video Instructions Manager */}
              {passageToEdit && editPassageText && (
                <Box sx={{
                  mt: 2,
                  p: 2,
                  background: 'rgba(156, 39, 176, 0.05)',
                  borderRadius: 2,
                  border: '1px solid rgba(156, 39, 176, 0.2)',
                  maxHeight: '350px',
                  overflow: 'auto'
                }}>
                  <LineVideoManager
                    passageId={passageToEdit._id}
                    passageText={editPassageText}
                    passageType={editPassageType}
                    lineVideos={editLineVideos}
                    onVideosChange={setEditLineVideos}
                    disabled={updatingPassage}
                  />
                </Box>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3, borderTop: '1px solid rgba(0,0,0,0.1)', pt: 2 }}>
          <Button onClick={() => {
            // Reset state and close
            setEditTextChanged(false);
            setLexileNeedsCalculation(false);
            setOriginalEditText('');
            setEditLineVideos([]);
            setEditHeroVideo(null);
            setEditPassageOpen(false);
          }} color="secondary" variant="outlined" sx={{ fontWeight: 'bold', borderRadius: 2, px: 6, py: 1.5, fontSize: 16 }}>Cancel</Button>
          <Button onClick={handleEditPassage} variant="contained" color="primary" disabled={!editPassageName.trim() || !editPassageText.trim() || !editPassageGradeLevel || !editPassageSource || (editPassageType === 'story' && !editPassageStoryType) || updatingPassage} sx={{ fontWeight: 'bold', borderRadius: 2, px: 6, py: 1.5, fontSize: 16, background: 'linear-gradient(90deg, #4fc3f7 0%, #81c784 100%)' }}>
            {updatingPassage ? (
              editTextChanged && lexileNeedsCalculation && calculatingLexile ? '⏳ Calculating Lexile...' : '⏳ Updating...'
            ) : (
              editTextChanged && lexileNeedsCalculation ? '🧮 Update & Calculate Lexile' : '✨ Update Passage'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={handleDeleteCancel}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            overflow: 'hidden',
            background: 'linear-gradient(135deg, #fff8f0 0%, #ffebee 100%)',
            boxShadow: '0 20px 60px rgba(244,67,54,0.2)'
          }
        }}
      >
        {passageToDelete && (
          <Box sx={{ width: '100%', height: 160, position: 'relative', background: '#ffebee' }}>
            <img
              src={getPassageImage(passageToDelete)}
              alt={passageToDelete.title}
              style={{ 
                width: '100%', 
                height: '100%', 
                objectFit: 'cover', 
                display: 'block',
                opacity: 0.7
              }}
            />
            <Box sx={{ 
              position: 'absolute', 
              left: 0, 
              right: 0, 
              bottom: 0, 
              top: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'linear-gradient(45deg, rgba(244,67,54,0.8) 0%, rgba(229,57,53,0.8) 100%)' 
            }}>
              <DeleteIcon sx={{ fontSize: 48, color: 'white' }} />
            </Box>
          </Box>
        )}
        <DialogTitle sx={{ 
          textAlign: 'center', 
          fontWeight: 700, 
          fontSize: 24, 
          mt: 2, 
          mb: 1, 
          color: '#d32f2f'
        }}>
          Archive Passage?
        </DialogTitle>
        <DialogContent sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          py: 3 
        }}>
          {passageToDelete && (
            <>
              <Typography 
                variant="h6" 
                fontWeight={600} 
                color="text.primary" 
                mb={2}
                textAlign="center"
              >
                "{passageToDelete.title}"
              </Typography>
              <Typography 
                mb={3} 
                color="text.secondary" 
                fontSize={16}
                textAlign="center"
                lineHeight={1.5}
              >
                This passage will be archived and removed from the dashboard. 
                Students will no longer be able to take tests for this passage.
              </Typography>
              <Box sx={{
                backgroundColor: 'rgba(244, 67, 54, 0.1)',
                borderRadius: 2,
                p: 2,
                border: '1px solid rgba(244, 67, 54, 0.3)',
                width: '100%',
                maxWidth: 400
              }}>
                <Typography 
                  variant="body2" 
                  color="#d32f2f"
                  textAlign="center"
                  fontWeight={500}
                >
                  ⚠️ This action cannot be undone
                </Typography>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ 
          justifyContent: 'center', 
          gap: 2,
          pb: 3, 
          px: 3 
        }}>
          <Button 
            onClick={handleDeleteCancel} 
            variant="outlined"
            sx={{ 
              fontWeight: 600, 
              borderRadius: 3, 
              px: 4, 
              py: 1.5,
              borderColor: '#9e9e9e',
              color: '#666',
              '&:hover': {
                borderColor: '#666',
                bgcolor: 'rgba(158,158,158,0.1)'
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteConfirm} 
            variant="contained" 
            disabled={deletingPassage}
            sx={{ 
              fontWeight: 600, 
              borderRadius: 3, 
              px: 4, 
              py: 1.5,
              bgcolor: '#d32f2f',
              '&:hover': {
                bgcolor: '#c62828'
              }
            }}
          >
            {deletingPassage ? 'Archiving...' : 'Archive Passage'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
} 